// src/production/Pre_Casting_Stages.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Settings, Plus, Save, X, Trash2, ChevronDown,
  CheckCircle2, AlertCircle, Package, FileText, Gem, Search,
  ClipboardList, Factory, Lock, Play, User, Clock, AlertTriangle, Info,
  Layers, Scale, FlaskConical, UploadCloud, ArrowLeft, Hammer, Users,
  GitMerge, Droplets, Filter, Flame,
  Loader2, Beaker, Camera, Menu
} from 'lucide-react';
import { productionApi } from './../api';
import SkeletonLoader from './components/SkeletonLoader';
import { BREAKPOINTS } from './productionTheme';

const PRE_STAGE_META = {
  WAX_CREATION: { title: 'Wax Creation', icon: Droplets },
  WAX_CLEANING: { title: 'Wax Cleaning', icon: Beaker },
  WAX_SETTING: { title: 'Wax Setting', icon: Gem },
  TREE_MAKING: { title: 'Tree Making', icon: GitMerge },
  POWDER_MIXING: { title: 'Powder Mixing', icon: FlaskConical },
  MOLDING: { title: 'Molding & Casting', icon: Flame },
  CASTING: { title: 'Casting', icon: Flame }
};

const PRE_STAGE_DISPLAY_ORDER = {
  WAX_CREATION: 1,
  WAX_CLEANING: 2,
  WAX_SETTING: 3,
  TREE_MAKING: 4,
  POWDER_MIXING: 5,
  MOLDING: 6,
  CASTING: 7,
};

const STAGE_ROLE_KEYWORDS = {
  WAX_CREATION: ['wax creation', 'wax specialist', 'wax'],
  WAX_CLEANING: ['wax cleaning', 'wax specialist', 'wax'],
  WAX_SETTING: ['wax setting', 'setting', 'wax'],
  TREE_MAKING: ['tree making', 'tree'],
  POWDER_MIXING: ['powder mixing', 'powder'],
  MOLDING: ['molding', 'moulding', 'casting'],
  CASTING: ['casting', 'casting process', 'caster'],
};

const getPreStageInfo = (stageCode, fallbackName) => PRE_STAGE_META[stageCode] || {
  title: fallbackName || (stageCode || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()),
  icon: Factory
};

const getNextPreStageTarget = (stageCode, stageFlow) => {
  const currentIndex = stageFlow.findIndex((stage) => stage.stage_code === stageCode);
  if (currentIndex === -1) return null;
  const nextStage = stageFlow[currentIndex + 1];
  return nextStage ? { route: '/production/pre-casting', stageCode: nextStage.stage_code } : null;
};

const normalizeStageStatus = (status) => status === 'CPMPLETED' ? 'COMPLETED' : (status || 'LOCKED');
const getStageStatusBucket = (status) => {
  const normalizedStatus = normalizeStageStatus(status);
  if (['LOCKED', 'PENDING'].includes(normalizedStatus)) return 'NOT_STARTED';
  if (normalizedStatus === 'IN_PROGRESS') return 'QC_EXECUTION';
  if (normalizedStatus === 'QC_PENDING') return 'QC_PENDING';
  if (['QC_APPROVED', 'COMPLETED'].includes(normalizedStatus)) return 'QC_APPROVED';
  return 'OTHER';
};
const STAGE_STATUS_FILTERS = [
  { key: 'ALL', label: 'All Stages' },
  { key: 'NOT_STARTED', label: 'Not Started' },
  { key: 'QC_EXECUTION', label: 'QC Execution' },
  { key: 'QC_PENDING', label: 'QC Pending' },
  { key: 'QC_APPROVED', label: 'QC Approved' },
  { key: 'BULK_STARTED', label: 'Bulk Started' }
];
const STAGE_STATUS_PRIORITY = {
  NOT_STARTED: 0,
  QC_EXECUTION: 1,
  QC_PENDING: 2,
  QC_APPROVED: 3,
  OTHER: 4
};

const STAGE_STATUS_META = {
  LOCKED: { tone: 'border-slate-200 bg-slate-100 text-slate-500', badge: 'Locked' },
  PENDING: { tone: 'border-yellow-500/50 bg-white text-slate-900', badge: 'Ready to Start' },
  IN_PROGRESS: { tone: 'border-blue-500 bg-white text-slate-900', badge: 'In Progress' },
  QC_PENDING: { tone: 'border-orange-500/60 bg-white text-slate-900', badge: 'Awaiting QC Review' },
  QC_APPROVED: { tone: 'border-green-500/50 bg-green-500/10 text-slate-900', badge: 'QC Approved' },
  QC_REJECTED: { tone: 'border-red-500/60 bg-red-500/10 text-slate-900', badge: 'Rework Required' }
};

const StageSkeletonGrid = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <SkeletonLoader count={6} height={132} />
  </div>
);

const formatStageDataLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const summarizeStageData = (summary = {}) => (
  Object.entries(summary)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .slice(0, 6)
);

const getStageRecordForCode = (job, stageCode) => {
  const allStages = job?.stage_statuses || [];
  const matchedStage = allStages.find((stage) => stage.stage_code === stageCode);
  if (matchedStage) return matchedStage;
  if (job?.current_stage?.stage_code === stageCode) return job.current_stage;
  if (job?.stage?.stage_code === stageCode) return job.stage;
  return null;
};

const getDisplayStageStatus = (stage, stageStatuses = []) => {
  if (!stage) return 'LOCKED';
  const normalizedStatus = normalizeStageStatus(stage.status);
  const hasNextStage = stageStatuses.some((candidate) => candidate.sequence_number > stage.sequence_number);
  return !hasNextStage && ['QC_APPROVED', 'COMPLETED'].includes(normalizedStatus) ? 'COMPLETED' : normalizedStatus;
};

const getOpenReworkPieces = (stage) => Number(stage?.open_rework_pieces || 0);

const dedupeStageNav = (stages = []) => {
  const seen = new Set();
  return stages.filter((stage) => {
    const key = normalizeStageCode(stage.stage_code || stage.stage_name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const sortPreStageNav = (stages = []) => (
  [...stages].sort((a, b) => {
    const stageA = normalizeStageCode(a.stage_code || a.stage_name);
    const stageB = normalizeStageCode(b.stage_code || b.stage_name);
    const orderA = PRE_STAGE_DISPLAY_ORDER[stageA] ?? 999;
    const orderB = PRE_STAGE_DISPLAY_ORDER[stageB] ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return (a.sequence_number || 0) - (b.sequence_number || 0);
  })
);

const buildDefaultPreStageNav = (stageCodes = []) => (
  stageCodes.map((stageCode) => {
    const info = getPreStageInfo(stageCode);
    return {
      stage_code: stageCode,
      stage_name: info.title,
      sequence_number: PRE_STAGE_DISPLAY_ORDER[stageCode] || 0,
      ...info,
    };
  })
);

const DEFAULT_PRE_STAGE_NAV = buildDefaultPreStageNav([
  'WAX_CREATION',
  'WAX_CLEANING',
  'WAX_SETTING',
  'TREE_MAKING',
  'POWDER_MIXING',
  'MOLDING',
]);

const DEFAULT_CASTING_STAGE_NAV = buildDefaultPreStageNav(['CASTING']);

const getStageId = (stage) => stage?.stage_id || stage?.id || null;
const normalizeStageCode = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
const normalizeWorkerRecord = (worker) => ({
  id: worker?.id || worker?.worker_id,
  worker_id: worker?.worker_id || worker?.id,
  first_name: worker?.first_name || worker?.name || '',
  last_name: worker?.last_name || '',
  name: worker?.name || `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim(),
  role: worker?.role || worker?.role_in_stage || '',
  current_active_tasks_count: worker?.current_active_tasks_count || 0,
});
const getWorkerIdentity = (worker) => String(worker?.worker_id || worker?.id || '');
const getWorkerDisplayName = (worker) => (
  worker?.name || `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim() || 'Unnamed Worker'
);
const formatWorkerOptionLabel = (worker) => {
  const name = getWorkerDisplayName(worker);
  const role = worker?.role || worker?.role_in_stage;
  const activeTasks = worker?.current_active_tasks_count ? ` (${worker.current_active_tasks_count} active)` : '';
  return `${name}${role ? ` | ${role}` : ''}${activeTasks}`;
};
const dedupeWorkers = (workers = []) => {
  const seen = new Set();
  return workers.filter((worker) => {
    const key = getWorkerIdentity(worker);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const workerMatchesStageRole = (worker, stageCode) => {
  const keywords = STAGE_ROLE_KEYWORDS[normalizeStageCode(stageCode)] || [];
  if (!keywords.length) return true;
  const roleValue = String(worker?.role || '').toLowerCase();
  return keywords.some((keyword) => roleValue.includes(keyword));
};

const mergeWorkerLists = (existingWorkers = [], incomingWorkers = []) => (
  dedupeWorkers([
    ...(existingWorkers || []).map(normalizeWorkerRecord),
    ...(incomingWorkers || []).map(normalizeWorkerRecord),
  ])
);
const STONE_ENABLED_STAGE_CODES = new Set(['WAX_SETTING']);
const shouldUseAssignedStones = (stageCode) => STONE_ENABLED_STAGE_CODES.has(normalizeStageCode(stageCode));
const normalizeStoneIssueRow = (stone = {}, isAssigned = false) => ({
  material_id: stone.material_id || '',
  stone_name: stone.stone_name || stone.name || '',
  stone_type: stone.stone_type || stone.type || stone.material_type || '',
  size: stone.size || '',
  count: stone.count || stone.qty || 1,
  quality_grade: stone.quality_grade || '',
  clarity: stone.clarity || '',
  cut: stone.cut || '',
  color: stone.color || '',
  unit: stone.unit || 'PCS',
  is_assigned: isAssigned,
  is_saved: isAssigned,
});

const dedupeAssignedMaterialRows = (rows = [], stock = []) => {
  const merged = new Map();
  rows.forEach((row) => {
    const materialId = row?.material_id;
    if (!materialId) return;
    const existing = merged.get(String(materialId));
    const qty = Number(row.qty || row.quantity || 0);
    if (existing) {
      existing.qty = qty > 0 ? qty : existing.qty;
      existing.is_assigned = true;
      existing.is_saved = true;
      return;
    }
    const match = stock.find((s) => String(s.material_id) === String(materialId));
    merged.set(String(materialId), {
      material_id: materialId,
      qty,
      stock_avail: match ? match.available_quantity : 0,
      unit: row.unit || match?.unit || 'g',
      is_assigned: true,
      is_saved: true,
      is_preassigned: !!row.is_preassigned,
    });
  });
  return Array.from(merged.values());
};
const normalizeNumericInput = (value) => {
  const cleaned = String(value || '').replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  if (cleaned === '.') return '0.';
  const [intPartRaw, ...decParts] = cleaned.split('.');
  const intPart = intPartRaw.replace(/^0+(?=\d)/, '') || '0';
  if (decParts.length === 0) return intPart;
  return `${intPart}.${decParts.join('').slice(0, 4)}`;
};
const isPieceUnit = (unit) => String(unit || '').trim().toUpperCase() === 'PCS';
const normalizeMaterialQtyInput = (value, unit) => {
  if (isPieceUnit(unit)) {
    return String(value || '').replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '') || '';
  }
  return normalizeNumericInput(value);
};

const parseStageDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const resolveStageDeadline = (stage) => {
  const rawExpected = stage?.process_data?.expected_end_time || stage?.expected_end_time;
  const parsedExpected = parseStageDate(rawExpected);
  if (parsedExpected) return parsedExpected;
  const startedAt = parseStageDate(stage?.started_at);
  const expectedMinutes = Number(stage?.expected_duration_minutes || 0);
  return startedAt && expectedMinutes > 0 ? new Date(startedAt.getTime() + expectedMinutes * 60 * 1000) : null;
};

const resolveOrderLookupInput = (inputValue, candidates = []) => {
  const typed = String(inputValue || '').trim();
  if (!typed) return null;
  const lowerTyped = typed.toLowerCase();

  const byOrder = candidates.find((item) => String(item.order_id || '').trim().toLowerCase() === lowerTyped);
  if (byOrder?.order_id) return String(byOrder.order_id);

  const byJobCardNo = candidates.find((item) => String(item.job_card_no || '').trim().toLowerCase() === lowerTyped);
  if (byJobCardNo?.order_id) return String(byJobCardNo.order_id);

  const byJobCardId = candidates.find((item) => String(item.job_card_id || '').trim().toLowerCase() === lowerTyped);
  if (byJobCardId?.order_id) return String(byJobCardId.order_id);

  return typed;
};

// --- API FUNCTIONS ---
const api = {
  // 1. Fetch Job Cards
  // âœ… FIX: order_id is returned at the top level of each item from /by-stage/stages
  // The backend returns: { job_card_id, job_card_no, design_code, quantity, order_id, stage: {...} }
  async fetchJobCards(stageCode) {
    try {
      if (stageCode === 'DELAYED_STAGES') return [];
      const response = await productionApi.get(`/job-cards/by-stage/stages?stage_code=${stageCode}`);
      const rawData = response.data.data;
      if (Array.isArray(rawData)) {
        return rawData.map(item => ({
          ...item,
          job_id: item.job_card_id,
          order_id: item.order_id || null,
          current_stage: item.stage || null,
          stages: {
            pre_casting: [item.stage],
            post_casting: [],
            casting: []
          },
          stage_statuses: []
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching job cards:', error);
      return [];
    }
  },

  // 2. Fetch Workers (Specific to stage)
  async fetchStageWorkers(stageId) {
    try {
      const response = await productionApi.get(`/job-cards/stages/${stageId}/workers`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching stage workers", error);
      return [];
    }
  },

  // 3. Fetch All Workers (For 'Show All' option)
  async fetchAllWorkers() {
    try {
      const response = await productionApi.get(`/job-cards/workers`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching all workers", error);
      return [];
    }
  },

  // 4. Start Stage
  async startStage(stageId, data) {
    const response = await productionApi.post(`/job-cards/stages/${stageId}/start`, data);
    return response.data;
  },

  // 5. Complete Stage
  async completeStage(stageId, data) {
    const response = await productionApi.post(`/job-cards/stages/${stageId}/complete`, data);
    return response.data;
  },

  // 5.5 Fetch Assigned Stage Workers
  async fetchAssignedStageWorkers(stageId) {
    try {
      const response = await productionApi.get(`/job-cards/stages/${stageId}/assigned-workers`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching assigned stage workers", error);
      return [];
    }
  },

  // 6. Fetch Global Stock
  async fetchMaterialsWithStock() {
    try {
      const response = await productionApi.get(`/job-cards/materials-with-stock`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching stock", error);
      return [];
    }
  },

  // 7. Fetch Assigned Materials (BOM from Design Master)
  async fetchAssignedStageMaterials(designCode, stageCode) {
    try {
      const dCode = encodeURIComponent(designCode);
      const sCode = encodeURIComponent(stageCode);
      const response = await productionApi.get(`/job-cards/designs/code/${dCode}/stages/${sCode}/materials`);
      return response.data.data || [];
    } catch (error) {
      return [];
    }
  },

  // 7.5. Bulk Material Check — aggregated stock for multiple stages
  async fetchBulkMaterialCheck(stageIds) {
    try {
      const params = stageIds.map(id => `stage_ids[]=${id}`).join('&');
      const response = await productionApi.get(`/job-cards/stages/bulk-material-check?${params}`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching bulk material check", error);
      return [];
    }
  },

  // 8. Fetch Stage Prefill Data
  async fetchStagePrefill(stageId) {
    try {
      const response = await productionApi.get(`/job-cards/stages/${stageId}/prefill`);
      return response.data;
    } catch (error) {
      console.error("Error fetching prefill", error);
      return { pre_fill_data: {}, before_weight: 0, pieces_in: 0 };
    }
  },

  // 8.5 Fetch issued materials for a stage (for calculations/return workflow)
  async fetchStageMaterials(stageId) {
    try {
      const response = await productionApi.get(`/job-cards/stages/${stageId}/materials`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching stage materials", error);
      return [];
    }
  },

  async fetchAssignedDesignStones(designCode) {
    try {
      const dCode = encodeURIComponent(designCode);
      const response = await productionApi.get(`/job-cards/designs/code/${dCode}/stones`);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching assigned stones", error);
      return [];
    }
  },

  async fetchInventoryStones() {
    try {
      const response = await productionApi.get('/inventory/stones');
      return response.data?.data || [];
    } catch (error) {
      console.error("Error fetching inventory stones", error);
      return [];
    }
  },

  // 9. Fetch Job Cards By Order
  async fetchJobCardsByOrder(orderId) {
    try {
      const response = await productionApi.get(`/job-cards/by-order/${orderId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching job cards by order:", error);
      return { success: false, data: [] };
    }
  },

  // 10. Create Tree Batch
  async combineTreeBatch(stageIds, maxPieces) {
    try {
      const response = await productionApi.post('/job-cards/stages/tree-batch/create', {
        stage_ids: stageIds,
        max_pieces_per_tree: maxPieces
      });
      return response.data;
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error combining tree' };
    }
  },

  // 10.5 Fetch stages attached to a tree batch
  async fetchTreeBatchStages(treeBatchId) {
    try {
      const response = await productionApi.get(`/job-cards/stages/tree-batch/${treeBatchId}/stages`);
      return response.data?.data || [];
    } catch (error) {
      console.error("Error fetching tree batch stages", error);
      return [];
    }
  },

  // 11. Upload Stage Photo
  async uploadStagePhoto(stageId, base64Data, type, remarks = "") {
    try {
      const payload = { photo_base64: base64Data, photo_type: type, remarks };
      const response = await productionApi.post(`/job-cards/stages/${stageId}/photos`, payload);
      return response.data;
    } catch (error) {
      console.error("Error uploading photo", error);
      return null;
    }
  },

  async fetchJobCardStageStatuses(jobCardId) {
    try {
      const response = await productionApi.get(`/job-cards/${jobCardId}/stages-with-status`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching job card stage statuses:', error);
      return [];
    }
  },

  async fetchStageCatalog() {
    try {
      const response = await productionApi.get('/job-cards/stages/catalog');
      return response.data.data || {};
    } catch (error) {
      console.error('Error fetching stage catalog:', error);
      return {};
    }
  },

  async fetchDelayedStages() {
    try {
      const response = await productionApi.get('/production/delayed-stages');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching delayed stages:', error);
      return [];
    }
  },

  // 12. Return Stage Materials
  async returnStageMaterials(stageId, returnsList) {
    const response = await productionApi.post(`/job-cards/stages/${stageId}/return-materials`, {
      returns: returnsList
    });
    return response.data;
  }
};

// --- HELPER COMPONENTS ---

const StatusBadge = ({ status, isDelayed }) => {
  const styles = {
    LOCKED: 'bg-slate-100 text-slate-500 border-slate-200',
    PENDING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse',
    QC_PENDING: 'bg-orange-50 text-orange-700 border-orange-200',
    QC_APPROVED: 'bg-green-50 text-green-700 border-green-200',
    COMPLETED: 'bg-green-50 text-green-700 border-green-200',
  };
  const normalizedStatus = status === 'CPMPLETED' ? 'COMPLETED' : status;

  if (isDelayed && normalizedStatus === 'IN_PROGRESS') {
    return (
      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase border bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
        <AlertTriangle size={10} /> Delayed
      </span>
    );
  }

  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${styles[normalizedStatus] || styles.LOCKED}`}>
      {normalizedStatus ? normalizedStatus.replace('_', ' ') : 'UNKNOWN'}
    </span>
  );
};

const CountdownAlert = ({ stage }) => {
  const deadline = resolveStageDeadline(stage);
  const [now, setNow] = useState(Date.now());

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!deadline || stage?.status !== 'IN_PROGRESS') return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline, stage?.status]);

  if (!deadline || stage?.status !== 'IN_PROGRESS') return null;

  const msRemaining = deadline.getTime() - now;
  const isOverdue = msRemaining < 0;
  const absoluteMinutes = Math.ceil(Math.abs(msRemaining) / 60000);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const timeLabel = `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;

  return (
    <div className={`rounded-xl border px-4 py-3 text-sm font-semibold ${isOverdue ? 'border-red-300 bg-red-50 text-red-700 animate-pulse' : absoluteMinutes <= 15 ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-sky-200 bg-sky-50 text-sky-700'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>{isOverdue ? `Deadline missed by ${timeLabel}` : `Time remaining: ${timeLabel}`}</span>
        <span className="text-xs uppercase tracking-wide">Deadline {deadline.toLocaleString()}</span>
      </div>
    </div>
  );
};

const DelayedStagesPanel = ({ delayedStages = [] }) => {
  const navigate = useNavigate();

  if (!delayedStages.length) {
    return (
      <div className="rounded-2xl border border-emerald-100 bg-white p-10 text-center text-slate-500">
        <CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={40} />
        No delayed stages right now.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Job Card</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3">Worker</th>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Deadline</th>
              <th className="px-4 py-3">Delay</th>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3 text-right">Timeline</th>
            </tr>
          </thead>
          <tbody>
            {delayedStages.map((row) => (
              <tr key={row.stage_id} className={row.severity === 'CRITICAL' ? 'bg-red-50/80' : 'bg-orange-50/70'}>
                <td className="px-4 py-3 font-semibold text-slate-900">{row.job_card_no}<div className="text-xs text-slate-500">{row.order_id || 'No order'}</div></td>
                <td className="px-4 py-3 text-slate-700">{row.stage_name}</td>
                <td className="px-4 py-3 text-slate-700">{row.worker_name || 'Unassigned'}</td>
                <td className="px-4 py-3 text-slate-600">{row.started_at ? new Date(row.started_at).toLocaleString() : '-'}</td>
                <td className="px-4 py-3 text-slate-600">{row.expected_end_time ? new Date(row.expected_end_time).toLocaleString() : '-'}</td>
                <td className="px-4 py-3 font-bold text-slate-900">{row.delay_minutes} min</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-1 text-xs font-bold ${row.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                    {row.severity}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => navigate(`/production/gantt?jobCardId=${row.job_card_id}`)} className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800">
                    View Timeline
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- MODALS ---

const StartStageModal = ({ isOpen, onClose, onStart, stage, job, allJobCards = [], bulkJobs = [] }) => {
  const [primaryWorkerId, setPrimaryWorkerId] = useState('');
  const [additionalWorkers, setAdditionalWorkers] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [beforeWeight, setBeforeWeight] = useState('');
  const [, setStartTime] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [workers, setWorkers] = useState([]);
  const [stockMaterials, setStockMaterials] = useState([]);
  const [inventoryStones, setInventoryStones] = useState([]);
  const [materialsToIssue, setMaterialsToIssue] = useState([]);
  const [stonesToIssue, setStonesToIssue] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [loadingStones, setLoadingStones] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isAllWorkersLoaded, setIsAllWorkersLoaded] = useState(false);
  const [stageWiseAssignedWorkers] = useState([]);
  const [prefillData, setPrefillData] = useState(null);
  const [prefillPieces, setPrefillPieces] = useState(0);
  const [priorStageSummary, setPriorStageSummary] = useState({});
  const [priorStageName, setPriorStageName] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [materialSearch, setMaterialSearch] = useState('');

  // Tree Batching State
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [selectedStageIds, setSelectedStageIds] = useState([]);
  const [maxPiecesPerTree, setMaxPiecesPerTree] = useState(50);
  const [, setLoadingRelated] = useState(false);
  const [extraOrderId, setExtraOrderId] = useState('');
  const bulkJobSignature = bulkJobs.map((entry) => `${entry.job_card_id}:${entry.stages?.pre_casting?.[0]?.stage_id || ''}`).join('|');
  const stageId = getStageId(stage);
  const isFirstFlowStage = normalizeStageCode(stage?.stage_code) === 'WAX_CREATION';
  const shouldShowInputWeight = !isFirstFlowStage;
  const incomingPieces = isFirstFlowStage ? Number(job?.quantity || 0) : Number(prefillPieces || 0);
  const extraPieces = Math.max(0, incomingPieces - Number(job?.quantity || 0));
  const stageShortCode = normalizeStageCode(stage?.stage_code || stage?.stage_name);
  const showAssignedStones = shouldUseAssignedStones(stageShortCode);
  const filteredWorkers = workers.filter((w) => {
    if (!workerSearch.trim()) return true;
    const query = workerSearch.toLowerCase();
    const haystack = [
      w.first_name,
      w.last_name,
      w.name,
      w.role,
      w.id,
      w.worker_id
    ].join(' ').toLowerCase();
    return haystack.includes(query);
  }).sort((a, b) => (a.current_active_tasks_count || 0) - (b.current_active_tasks_count || 0));
  const filteredStockMaterials = stockMaterials.filter((m) => {
    if (!materialSearch.trim()) return true;
    const query = materialSearch.toLowerCase();
    const haystack = [m.material_name, m.material_code, m.code, m.material_id].join(' ').toLowerCase();
    return haystack.includes(query);
  });
  const assignedMaterialRows = materialsToIssue.filter((row) => row.is_assigned);
  const assignedStoneRows = stonesToIssue.filter((row) => row.is_assigned);
  const inventoryStoneOptions = inventoryStones.filter((stone) => !!(stone?.name || '').trim());
  const pendingTreeCandidates = (allJobCards || []).filter((entry) =>
    entry?.stages?.pre_casting?.[0]?.stage_code === 'TREE_MAKING' &&
    entry?.stages?.pre_casting?.[0]?.status === 'PENDING'
  );
  const extraLookupSuggestions = Array.from(new Set(
    pendingTreeCandidates.flatMap((entry) => [
      entry.order_id ? `Order: ${entry.order_id}` : null,
      entry.job_card_no ? `JC: ${entry.job_card_no}` : null
    ]).filter(Boolean)
  ));

  const formatDateTimeLocal = (date) => {
    const pad = (n) => n < 10 ? '0' + n : n;
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Intentionally keyed to stage/open identity so user-entered values are not reset by helper re-creations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen && stage && job) {
      setPrimaryWorkerId('');
      setAdditionalWorkers([]);
      setRemarks('');
      setBeforeWeight('');
      setIsAllWorkersLoaded(false);
      setPrefillData(null);
      setPrefillPieces(0);
      setPriorStageSummary({});
      setPriorStageName('');
      setWorkerSearch('');
      setMaterialSearch('');
      setStonesToIssue([]);
      setExtraOrderId('');
      const initialBulkRelated = bulkJobs
        .filter((entry) => entry.job_card_id !== job.job_card_id)
        .map((entry) => ({
          ...entry,
          stage_id: entry.stages?.pre_casting?.[0]?.stage_id,
          pieces_in: entry.stages?.pre_casting?.[0]?.pieces_in || entry.quantity
        }))
        .filter((entry) => entry.stage_id);
      setRelatedJobs(initialBulkRelated);
      setSelectedStageIds(
        stage.stage_code === 'TREE_MAKING' && bulkJobs.length > 0
          ? bulkJobs.map((entry) => entry.stages?.pre_casting?.[0]?.stage_id).filter(Boolean)
          : (stageId ? [stageId] : [])
      );
      const now = new Date();
      setStartTime(formatDateTimeLocal(now));
      const end = new Date(now.getTime() + (4 * 60 * 60 * 1000));
      setExpectedEndDate(formatDateTimeLocal(end));
      loadWorkers();
      loadMaterialContext();
      loadStoneContext();
      // Fetch prefill data from previous stage
      loadPrefillData();

      if (stage.stage_code === 'TREE_MAKING' && job.order_id && bulkJobs.length === 0 && stageId) {
        fetchRelatedJobs(job.order_id, stageId);
      }
    }
  }, [bulkJobSignature, isOpen, job?.job_card_id, job?.order_id, stage?.stage_code, stageId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchRelatedJobs = async (orderId, currentStageId) => {
    setLoadingRelated(true);
    try {
      const others = allJobCards.filter(j =>
        String(j.order_id || '') === String(orderId || '') &&
        j.stages?.pre_casting?.[0]?.stage_id !== currentStageId &&
        j.stages?.pre_casting?.[0]?.stage_code === 'TREE_MAKING' &&
        j.stages?.pre_casting?.[0]?.status === 'PENDING'
      );

      const enrichedOthers = await Promise.all(others.map(async (j) => {
        const stageId = j.stages.pre_casting[0].stage_id;
        const prefill = await api.fetchStagePrefill(stageId);
        return {
          ...j,
          stage_id: stageId,
          pieces_in: prefill?.pieces_in || j.quantity
        };
      }));
      setRelatedJobs(enrichedOthers);
    } catch (e) {
      console.error("Failed to load related jobs", e);
    } finally {
      setLoadingRelated(false);
    }
  };

  const fetchExtraOrderJobs = async () => {
    if (!extraOrderId || stage?.stage_code !== 'TREE_MAKING') return;
    setLoadingRelated(true);
    try {
      const rawLookup = extraOrderId.replace(/^order:\s*/i, '').replace(/^jc:\s*/i, '').trim();
      const resolvedOrderId = resolveOrderLookupInput(rawLookup, pendingTreeCandidates);
      const response = await api.fetchJobCardsByOrder(resolvedOrderId);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const enrichedOthers = rows
        .map((entry) => {
          const treeStage = entry.stages?.pre_casting?.find?.((row) => row.stage_code === 'TREE_MAKING') || entry.stages?.pre_casting?.[0];
          if (!treeStage || treeStage.status !== 'PENDING') return null;
          return {
            ...entry,
            stage_id: treeStage.stage_id,
            pieces_in: treeStage.pieces_in || entry.quantity
          };
        })
        .filter(Boolean);
      setRelatedJobs((prev) => {
        const merged = [...prev];
        enrichedOthers.forEach((item) => {
          if (!merged.some((row) => row.stage_id === item.stage_id) && item.stage_id !== stageId) {
            merged.push(item);
          }
        });
        return merged;
      });
      if (enrichedOthers.length === 0) {
        alert(`No startable TREE_MAKING job cards found for "${rawLookup}"`);
      }
      setExtraOrderId('');
    } catch (error) {
      console.error('Failed to load additional order jobs', error);
    } finally {
      setLoadingRelated(false);
    }
  };

  const loadPrefillData = useCallback(async () => {
    if (!stageId) return;
    const data = await api.fetchStagePrefill(stageId);
    if (data && data.success !== false) {
      setPrefillData(data.pre_fill_data || {});
      const derivedPieces = data.pieces_in || (isFirstFlowStage ? Number(job?.quantity || 0) : 0);
      setPrefillPieces(derivedPieces);
      const priorSummary = data.prior_stage_summary || {};
      setPriorStageSummary(priorSummary);
      setPriorStageName(data.prior_stage_name || '');
      if (shouldShowInputWeight) {
        const fallbackAfterWeight =
          priorSummary.after_weight ||
          priorSummary.output_weight ||
          priorSummary.gross_weight ||
          priorSummary.final_weight;
        const resolvedBeforeWeight = (data.before_weight && Number(data.before_weight) > 0)
          ? data.before_weight
          : fallbackAfterWeight;
        setBeforeWeight(resolvedBeforeWeight && Number(resolvedBeforeWeight) > 0 ? String(resolvedBeforeWeight) : '');
      } else {
        setBeforeWeight('');
      }
    }
  }, [isFirstFlowStage, job?.quantity, shouldShowInputWeight, stageId]);

  const loadWorkers = useCallback(async () => {
    if (!stageId) return;
    setLoadingWorkers(true);
    try {
      const stageShortCode = normalizeStageCode(stage?.stage_code || stage?.stage_name);
      const [stageWorkersResponse, assignedWorkersResponse] = await Promise.all([
        api.fetchStageWorkers(stageId),
        api.fetchAssignedStageWorkers(stageId),
      ]);
      const flattenedAssigned = dedupeWorkers([
        ...(stageWorkersResponse || []),
        ...(assignedWorkersResponse || []),
      ].map((worker) => normalizeWorkerRecord({
        ...worker,
        id: worker?.id || worker?.worker_id,
        worker_id: worker?.worker_id || worker?.id,
        name: worker?.name || `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim(),
        first_name: worker?.first_name || worker?.name || '',
        last_name: worker?.last_name || '',
        role: worker?.role || worker?.role_in_stage || '',
        role_in_stage: worker?.role_in_stage || worker?.role || '',
        stageShortCode,
        source: 'stage',
      })).filter((worker) => workerMatchesStageRole(worker, stageShortCode)));
      setWorkers(flattenedAssigned);
    } finally {
      setLoadingWorkers(false);
    }
  }, [stage?.stage_code, stage?.stage_name, stageId]);

  const loadMaterialContext = useCallback(async () => {
    setLoadingMaterials(true);
    try {
      const stock = await api.fetchMaterialsWithStock();
      setStockMaterials(stock);
      const sCode = normalizeStageCode(stage.stage_code || stage.stage_name);
      const assigned = await api.fetchAssignedStageMaterials(job.design_code, sCode);
      let initialRows = dedupeAssignedMaterialRows(
        (assigned || []).map((a) => ({
          material_id: a.material_id,
          qty: a.qty,
          unit: a.unit,
          is_assigned: true,
          is_saved: true,
          is_preassigned: false,
        })),
        stock
      );

      if (initialRows.length === 0) {
        const stageMaterials = stageId ? await api.fetchStageMaterials(stageId) : [];
        initialRows = dedupeAssignedMaterialRows(
          (Array.isArray(stageMaterials) ? stageMaterials : [])
            .filter((row) => Number(row.qty_reserved || 0) > 0)
            .map((row) => ({
              material_id: row.material_id,
              qty: row.qty_reserved,
              unit: row.unit,
              is_assigned: true,
              is_saved: true,
              is_preassigned: true,
            })),
          stock
        );
      }

      if (initialRows.length === 0) initialRows.push({ material_id: '', qty: '', stock_avail: 0, unit: 'g', is_assigned: false, is_saved: false });
      setMaterialsToIssue(initialRows);
    } catch (e) {
      console.error("Failed to load material context", e);
    } finally {
      setLoadingMaterials(false);
    }
  }, [job?.design_code, stage?.stage_code, stage?.stage_name, stageId]);

  const loadStoneContext = useCallback(async () => {
    if (!showAssignedStones || !job?.design_code) {
      setStonesToIssue([]);
      return;
    }
    setLoadingStones(true);
    try {
      const [assigned, availableStones] = await Promise.all([
        api.fetchAssignedDesignStones(job.design_code),
        api.fetchInventoryStones(),
      ]);
      setInventoryStones(availableStones || []);
      const rows = (assigned || []).map((stone) => normalizeStoneIssueRow(stone, true));
      setStonesToIssue(rows.length > 0 ? rows : [normalizeStoneIssueRow({}, false)]);
    } catch (e) {
      console.error("Failed to load stone context", e);
      setInventoryStones([]);
      setStonesToIssue([normalizeStoneIssueRow({}, false)]);
    } finally {
      setLoadingStones(false);
    }
  }, [job?.design_code, showAssignedStones]);

  const handleWorkerSelect = async (e) => {
    const val = e.target.value;
    if (val === 'LOAD_ALL') {
      setLoadingWorkers(true);
      try {
        const allWorkers = await api.fetchAllWorkers();
        setWorkers(mergeWorkerLists(workers, allWorkers));
        setIsAllWorkersLoaded(true);
      } catch (error) {
        alert("Failed to load all workers");
      } finally {
        setLoadingWorkers(false);
      }
    } else {
      setPrimaryWorkerId(val);
    }
  };

  const handleLoadAllWorkers = async () => {
    setLoadingWorkers(true);
    try {
      const allWorkers = await api.fetchAllWorkers();
      setWorkers(mergeWorkerLists(workers, allWorkers));
      setIsAllWorkersLoaded(true);
    } catch (error) {
      alert("Failed to load all workers");
    } finally {
      setLoadingWorkers(false);
    }
  };

  const handleAddWorker = () => {
    setAdditionalWorkers([...additionalWorkers, { worker_id: '', role_in_stage: 'Assistant', is_saved: false }]);
  };

  const handleRemoveWorker = (index) => {
    setAdditionalWorkers(additionalWorkers.filter((_, i) => i !== index));
  };

  const handleAdditionalWorkerChange = (index, field, value) => {
    const newWorkers = [...additionalWorkers];
    newWorkers[index][field] = value;
    newWorkers[index].is_saved = false;
    setAdditionalWorkers(newWorkers);
  };

  const saveAdditionalWorker = (index) => {
    const next = [...additionalWorkers];
    if (!next[index]?.worker_id) return;
    next[index].is_saved = true;
    setAdditionalWorkers(next);
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...materialsToIssue];
    newRows[index][field] = field === 'qty'
      ? normalizeMaterialQtyInput(value, newRows[index]?.unit)
      : value;
    newRows[index].is_saved = false;
    if (field === 'material_id') {
      const match = stockMaterials.find(s => s.material_id.toString() === value.toString());
      newRows[index].stock_avail = match ? match.available_quantity : 0;
      newRows[index].unit = match ? match.unit : 'g';
      newRows[index].qty = normalizeMaterialQtyInput(newRows[index].qty, newRows[index].unit);
    }
    setMaterialsToIssue(newRows);
  };

  const addMaterialRow = () => {
    setMaterialsToIssue([...materialsToIssue, { material_id: '', qty: '', stock_avail: 0, unit: 'g', is_assigned: false, is_saved: false }]);
  };

  const handleStoneRowChange = (index, field, value) => {
    const nextRows = [...stonesToIssue];
    nextRows[index][field] = value;
    nextRows[index].is_saved = false;
    setStonesToIssue(nextRows);
  };

  const handleStoneSelect = (index, selectedName) => {
    const selectedStone = inventoryStoneOptions.find((stone) => String(stone.name || '') === String(selectedName || ''));
    if (!selectedStone) {
      handleStoneRowChange(index, 'stone_name', selectedName);
      return;
    }

    const nextRows = [...stonesToIssue];
    nextRows[index] = {
      ...nextRows[index],
      ...normalizeStoneIssueRow(selectedStone, nextRows[index]?.is_assigned),
      stone_name: selectedStone.name || '',
      is_saved: false,
    };
    setStonesToIssue(nextRows);
  };

  const addStoneRow = () => {
    setStonesToIssue([...stonesToIssue, normalizeStoneIssueRow({}, false)]);
  };

  const removeStoneRow = (index) => {
    setStonesToIssue(stonesToIssue.filter((_, i) => i !== index));
  };

  const saveStoneRow = (index) => {
    const nextRows = [...stonesToIssue];
    const row = nextRows[index];
    if (!row?.stone_name || !Number(row.count)) return;
    row.is_saved = true;
    setStonesToIssue(nextRows);
    if (index === nextRows.length - 1) {
      addStoneRow();
    }
  };

  const removeMaterialRow = (index) => {
    const newRows = materialsToIssue.filter((_, i) => i !== index);
    setMaterialsToIssue(newRows);
  };

  const saveMaterialRow = (index) => {
    const nextRows = [...materialsToIssue];
    const row = nextRows[index];
    if (!row?.material_id || !Number(row.qty)) return;
    row.is_saved = true;
    setMaterialsToIssue(nextRows);
    if (index === nextRows.length - 1) {
      addMaterialRow();
    }
  };

  if (!isOpen) return null;

  const totalTreePieces = (() => {
    let total = prefillPieces || 0;
    relatedJobs.forEach(rj => { if (selectedStageIds.includes(rj.stage_id)) total += (rj.pieces_in || 0); });
    return total;
  })();
  const treesNeeded = Math.max(1, Math.ceil(totalTreePieces / Number(maxPiecesPerTree || 50)));
  const utilizationPct = totalTreePieces > 0 ? (totalTreePieces / (treesNeeded * Number(maxPiecesPerTree || 50))) * 100 : 0;

  const handleConfirm = async () => {
    if (!primaryWorkerId) return;
    setLoadingSubmit(true);
    try {
      if (stage.stage_code === 'TREE_MAKING' && selectedStageIds.length > 1) {
        const tbRes = await api.combineTreeBatch(selectedStageIds, Number(maxPiecesPerTree));
        if (tbRes && tbRes.success) {
          console.log('Tree batch created:', tbRes.batch_id);
        }
      }

      const validMaterials = materialsToIssue.filter(m => m.material_id && Number(m.qty) > 0);
      const validStones = stonesToIssue
        .filter((stone) => stone.stone_name && Number(stone.count) > 0)
        .map((stone) => ({
          material_id: stone.material_id ? Number(stone.material_id) : null,
          stone_name: stone.stone_name,
          stone_type: stone.stone_type,
          size: stone.size,
          count: Number(stone.count),
          quality_grade: stone.quality_grade,
          clarity: stone.clarity,
          cut: stone.cut,
          color: stone.color,
          unit: stone.unit || 'PCS',
        }));
      const validAdditionalWorkers = additionalWorkers.filter(aw => aw.worker_id);

      // Check if any selected material has 0 or insufficient stock
      const outOfStockMaterials = validMaterials.filter(m => Number(m.stock_avail || 0) <= 0);
      if (outOfStockMaterials.length > 0) {
        const names = outOfStockMaterials.map(m => {
          const match = stockMaterials.find(s => String(s.material_id) === String(m.material_id));
          return match?.material_name || `Material #${m.material_id}`;
        }).join(', ');
        alert(`The issued materials (${names}) are out of stock. Please contact the inventory manager or admin to update the stock data.`);
        setLoadingSubmit(false);
        return;
      }

      const insufficientMaterials = validMaterials.filter(m => Number(m.qty) > Number(m.stock_avail || 0));
      if (insufficientMaterials.length > 0) {
        const details = insufficientMaterials.map(m => {
          const match = stockMaterials.find(s => String(s.material_id) === String(m.material_id));
          return `${match?.material_name || m.material_id}: need ${m.qty}, available ${m.stock_avail || 0}`;
        }).join('\n');
        alert(`Insufficient stock for the following materials:\n${details}\n\nPlease contact the inventory manager or admin to update the stock data.`);
        setLoadingSubmit(false);
        return;
      }

      await onStart({
        worker_id: primaryWorkerId,
        additional_workers: validAdditionalWorkers,
        expected_end_time: expectedEndDate,
        before_weight: beforeWeight || 0,
        remarks: remarks,
        issued_materials: validMaterials,
        issued_stones: validStones,
        batch_stage_ids: stage.stage_code === 'TREE_MAKING' && selectedStageIds.length > 1 ? selectedStageIds : null,
        stage_ids: bulkJobs.length > 0 ? bulkJobs.map((entry) => getStageId(getStageRecordForCode(entry, stage?.stage_code) || entry.current_stage || entry.stage)).filter(Boolean) : null
      });
      onClose();
    } catch (error) {
      console.error("Failed to start", error);
      const detailRows = error?.response?.data?.details || [];
      const detailText = detailRows.length > 0
        ? `\n${detailRows.map((row) => `${row.material_name}: need ${row.required_qty}, available ${row.available_qty} ${row.unit || ''}`.trim()).join('\n')}`
        : '';
      const errMsg = (error?.response?.data?.message || error?.message || "Failed to start stage.") + detailText;
      alert(errMsg);
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-0 backdrop-blur-sm sm:p-4">
      <div className="pre-mobile-modal flex h-full w-full flex-col bg-white text-slate-900 shadow-2xl animate-in fade-in zoom-in duration-200 sm:h-auto sm:max-h-[92vh] sm:max-w-[980px] sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white p-5 sm:rounded-t-2xl">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{bulkJobs.length > 0 ? `Bulk Start ${stage?.stage_name}` : `Start ${stage?.stage_name}`}</h3>
            {/* âœ… order_id shown in modal header */}
            <p className="text-xs text-slate-400">
              Job Card: {job?.job_card_no}
              {job?.order_id && (
                <span className="ml-2 font-medium text-blue-600">| Order: {job.order_id}</span>
              )}
              {bulkJobs.length > 0 && <span className="ml-2 font-medium text-[#3B82F6]">| {bulkJobs.length} job cards selected</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Prefill Info Banners */}
          {prefillData && Object.keys(prefillData).length > 0 && (
            <div className="flex items-start gap-3 rounded-xl border border-cyan-200 bg-cyan-50 p-3">
              <Info size={18} className="mt-0.5 shrink-0 text-cyan-600" />
              <div>
                <p className="text-xs font-bold text-cyan-800">Auto-filled from {priorStageName || 'previous stage'}</p>
                <p className="mt-0.5 text-[11px] text-cyan-700">
                  Suggested values were carried forward from the prior stage and can still be edited.
                  {prefillData.note && <span className="mt-1 block italic">{prefillData.note}</span>}
                </p>
              </div>
            </div>
          )}
          {Object.keys(priorStageSummary).length > 0 && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-indigo-700">Received From Previous Stage</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {summarizeStageData(priorStageSummary).map(([key, value]) => (
                  <div key={key} className="rounded-lg border border-indigo-100 bg-white p-3">
                    <div className="text-[11px] uppercase tracking-wide text-indigo-500">{formatStageDataLabel(key)}</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{String(value)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {incomingPieces > 0 && (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <Package size={18} className="shrink-0 text-emerald-600" />
              <p className="text-xs font-bold text-emerald-800">
                {isFirstFlowStage ? 'Target production quantity:' : 'Received from previous stage:'}{' '}
                <span className="text-sm text-emerald-700">{incomingPieces} pieces</span>
              </p>
            </div>
          )}
          {!isFirstFlowStage && extraPieces > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Extra carry-forward detected: <span className="font-bold">+{extraPieces} pieces</span> above job card quantity ({job?.quantity || 0}).
            </div>
          )}

          {stage?.stage_code === 'TREE_MAKING' && relatedJobs.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="text-sm font-bold text-indigo-900 mb-3 flex items-center gap-2 border-b border-indigo-200 pb-2">
                <GitMerge size={16} /> Combine with other jobs for Order: {job?.order_id}
              </h4>
              <div className="flex items-end gap-3 mb-4">
                <div className="flex-1">
                  <label className="text-xs font-bold text-slate-500 block mb-1">Add Another Order ID</label>
                  <input
                    type="text"
                    value={extraOrderId}
                    onChange={(e) => setExtraOrderId(e.target.value)}
                    className="w-full p-2 border rounded bg-white text-sm"
                    placeholder="Type Order ID or Job Card ID"
                    list="tree-extra-lookup-suggestions"
                  />
                  <datalist id="tree-extra-lookup-suggestions">
                    {extraLookupSuggestions.map((value) => (
                      <option key={value} value={value} />
                    ))}
                  </datalist>
                </div>
                <button
                  type="button"
                  onClick={fetchExtraOrderJobs}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
                >
                  Add Order
                </button>
              </div>
              <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                <label className="flex items-center gap-3 p-2 bg-white rounded border border-indigo-300 opacity-70">
                  <input type="checkbox" checked disabled className="w-4 h-4 text-indigo-600 rounded border-slate-300" />
                  <div className="flex-1 text-sm font-bold text-slate-800">
                    {job.job_card_no} (Current)
                    <div className="text-xs text-slate-500 font-normal">Available Pieces: <span className="font-bold">{prefillPieces}</span></div>
                  </div>
                </label>
                {relatedJobs.map(rj => (
                  <label key={rj.job_card_id} className="flex items-center gap-3 p-2 bg-white rounded border cursor-pointer hover:bg-slate-50 border-slate-200">
                    <input
                      type="checkbox"
                      checked={selectedStageIds.includes(rj.stage_id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedStageIds(prev => [...prev, rj.stage_id]);
                        else setSelectedStageIds(prev => prev.filter(id => id !== rj.stage_id));
                      }}
                      className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                    <div className="flex-1 text-sm font-bold text-slate-800">
                      {rj.job_card_no} <span className="text-slate-500 font-normal">({rj.design_code})</span>
                      <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">{rj.order_id || 'No Order'}</span>
                      <div className="text-xs text-slate-500 font-normal">Available Pieces: <span className="font-bold text-indigo-600">{rj.pieces_in}</span></div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-4 bg-white p-3 rounded border text-sm">
                <div className="flex-1">
                  <label className="text-xs font-bold text-slate-500 block mb-1">Max Pieces / Tree</label>
                  <input type="number" className="w-full p-1.5 border rounded" value={maxPiecesPerTree} onChange={e => setMaxPiecesPerTree(e.target.value)} />
                </div>
                <div className="flex-1 text-center border-x px-2">
                  <div className="text-xs font-bold text-slate-500 mb-1">Total Pieces</div>
                  <div className="font-bold text-indigo-700 text-lg">{totalTreePieces}</div>
                </div>
                <div className="flex-1 text-center px-2">
                  <div className="text-xs font-bold text-slate-500 mb-1">Trees Needed</div>
                  <div className="font-bold text-slate-800 text-lg">{treesNeeded}</div>
                </div>
                <div className="flex-1 text-center border-l px-2">
                  <div className="text-xs font-bold text-slate-500 mb-1">Utilization</div>
                  <div className="font-bold text-slate-800 text-lg"><span className={utilizationPct < 70 ? "text-red-500" : "text-emerald-600"}>{utilizationPct.toFixed(1)}%</span></div>
                </div>
              </div>

              <div className={`mt-3 p-2.5 rounded-lg text-xs border flex items-start gap-2 ${totalTreePieces < 50 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <div>
                  <b>{totalTreePieces < 50 ? 'Below Recommended Threshold' : 'Tree Planning Ready'}</b>
                  <p>{totalTreePieces < 50 ? 'Recommended minimum is 50 pieces per tree, but you can still start with insufficient pieces or add jobs from another order.' : `${treesNeeded} tree(s) needed at the current piece count.`}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="flex justify-between items-center bg-indigo-50 p-2 rounded-lg border border-indigo-100">
                  <label className="text-sm font-bold text-indigo-900 flex items-center gap-2">Assign Workers <span className="text-red-500">*</span></label>
                  <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full font-medium">
                    {1 + additionalWorkers.filter(w => w.worker_id).length} workers assigned
                  </span>
                </div>

                <div className="space-y-3">
                  {false && stageWiseAssignedWorkers.length > 0 && (
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3">
                      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-indigo-700">
                        <Layers size={13} /> Stage Wise Workers
                      </div>
                      <div className="space-y-2">
                        {stageWiseAssignedWorkers.map((entry) => (
                          <div key={entry.stageName} className="rounded-lg border border-indigo-100 bg-white px-3 py-2">
                            <div className="text-xs font-bold text-slate-700">{entry.stageName}</div>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {entry.stageWorkers.map((worker) => (
                                <span
                                  key={worker.key}
                                  className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-semibold text-indigo-700"
                                >
                                  <User size={11} />
                                  {worker.name}
                                  {worker.stageShortCode ? ` · ${worker.stageShortCode}` : ''}
                                  {worker.role ? ` · ${worker.role}` : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 items-center">
                    <div className="w-24 shrink-0 text-xs font-bold text-slate-500 text-right">Search:</div>
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <input
                        type="text"
                        list="pre-worker-search-suggestions"
                        value={workerSearch}
                        onChange={(e) => setWorkerSearch(e.target.value)}
                        placeholder="Worker name / worker code"
                        className="w-full rounded border border-slate-300 bg-white pl-8 pr-2 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <datalist id="pre-worker-search-suggestions">
                        {workers.slice(0, 100).map((w) => (
                          <option key={`ws-${w.id || w.worker_id}`} value={`${(w.first_name || w.name || '').trim()} ${w.last_name || ''}`.trim()}>
                            {w.role || ''}
                          </option>
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="w-24 shrink-0 text-xs font-bold text-slate-500 text-right">Primary Worker:</div>
                    <div className="relative flex-1">
                      <select
                        className="w-full p-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none bg-white appearance-none"
                        value={primaryWorkerId}
                        onChange={handleWorkerSelect}
                        disabled={loadingSubmit || loadingWorkers}
                      >
                        <option value="">-- Select Staff --</option>
                        {filteredWorkers.map(w => (
                          <option key={w.id || w.worker_id} value={w.id || w.worker_id}>
                            {formatWorkerOptionLabel(w)}
                          </option>
                        ))}
                        {!isAllWorkersLoaded && <option value="LOAD_ALL" className="font-bold text-indigo-600">+ Show All Workers</option>}
                      </select>
                      {loadingWorkers ? (
                        <div className="absolute right-3 top-2.5"><Loader2 size={14} className="animate-spin text-indigo-600" /></div>
                      ) : (
                        <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={14} />
                      )}
                    </div>
                  </div>

                  {!isAllWorkersLoaded && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleLoadAllWorkers}
                        disabled={loadingWorkers}
                        className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loadingWorkers ? <Loader2 size={12} className="animate-spin" /> : <Users size={12} />}
                        Show Remaining Workers
                      </button>
                    </div>
                  )}

                  {additionalWorkers.map((aw, idx) => (
                    <div key={idx} className="flex gap-2 items-center animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="w-24 shrink-0">
                        <select
                          className="w-full p-2 text-xs border border-slate-300 rounded outline-none bg-slate-50 text-slate-700 font-medium"
                          value={aw.role_in_stage}
                          onChange={e => handleAdditionalWorkerChange(idx, 'role_in_stage', e.target.value)}
                        >
                          <option value="Assistant">Assistant</option>
                          <option value="Trainee">Trainee</option>
                          <option value="Co-Worker">Co-Worker</option>
                        </select>
                      </div>
                      <div className="relative flex-1">
                        <select
                          className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white"
                          value={aw.worker_id}
                          onChange={e => handleAdditionalWorkerChange(idx, 'worker_id', e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {filteredWorkers.map(w => (
                            <option key={w.id || w.worker_id} value={w.id || w.worker_id} disabled={w.id === Number(primaryWorkerId) || w.worker_id === Number(primaryWorkerId)}>
                              {formatWorkerOptionLabel(w)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => saveAdditionalWorker(idx)}
                        className={`p-2 rounded ${aw.is_saved ? 'text-emerald-600 bg-emerald-50' : 'text-indigo-600 hover:bg-indigo-50'}`}
                        title={aw.is_saved ? 'Added' : 'Add Worker'}
                      >
                        <Save size={16} />
                      </button>
                      <button onClick={() => handleRemoveWorker(idx)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                        <X size={16} />
                      </button>
                    </div>
                  ))}

                  <div className="flex justify-end pr-8">
                    <button
                      onClick={handleAddWorker}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-indigo-50 px-3 py-1.5 rounded-full"
                    >
                      <Plus size={12} /> Add another worker
                    </button>
                  </div>
                </div>
              </div>

              {shouldShowInputWeight ? (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    Input Weight (g)
                    {beforeWeight && priorStageName && (
                      <span className="rounded-full bg-cyan-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cyan-700">
                        Pre-filled from previous stage
                      </span>
                    )}
                  </label>
                  <input
                    type="number" step="0.01"
                    className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-slate-900 outline-none"
                    placeholder="0.00" value={beforeWeight}
                    onChange={(e) => setBeforeWeight(normalizeNumericInput(e.target.value))}
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  Input weight will be captured after <span className="font-bold">Wax Creation</span> is completed.
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Expected Delivery</label>
                <input
                  type="datetime-local"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none bg-white text-sm"
                  value={expectedEndDate}
                  onChange={(e) => setExpectedEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-col h-full min-h-[250px]">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Package size={16} /> Issue Materials
                </label>
                <button onClick={addMaterialRow} className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium">
                  <Plus size={14} /> Add Row
                </button>
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                <input
                  type="text"
                  list="pre-material-search-suggestions"
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  placeholder="Search material name / code"
                  className="w-full rounded border border-slate-300 bg-white pl-8 pr-2 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <datalist id="pre-material-search-suggestions">
                  {stockMaterials.slice(0, 150).map((m) => (
                    <option key={`ms-${m.material_id}`} value={m.material_name || ''}>
                      {m.material_code || m.code || m.material_id}
                    </option>
                  ))}
                </datalist>
              </div>
              {assignedMaterialRows.length > 0 && (
                <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-700">Assigned Design Materials</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {assignedMaterialRows.map((row, idx) => {
                      const material = stockMaterials.find((m) => String(m.material_id) === String(row.material_id));
                      return (
                        <div key={`${row.material_id}-${idx}`} className="rounded border border-indigo-100 bg-white p-2 text-sm">
                          <div className="font-semibold text-slate-900 break-all">{material?.material_name || row.material_id}</div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                            <span>Code: <b>{material?.material_code || material?.code || row.material_id}</b></span>
                            <span>Assigned Qty: <b>{row.qty} {row.unit}</b></span>
                            <span>Stock: <b>{row.stock_avail || 0} {row.unit}</b></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {loadingMaterials ? (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-xs gap-2">
                  <Loader2 className="animate-spin" size={14} /> Checking Inventory...
                </div>
              ) : (
                <div className="space-y-2 overflow-y-auto max-h-[250px] pr-1">
                  {materialsToIssue.map((row, idx) => (
                    <div key={idx} className="flex gap-2 items-start bg-white p-2 rounded border border-slate-200 shadow-sm">
                      <div className="flex-1 space-y-1">
                        <select
                          className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white"
                          value={row.material_id}
                          onChange={(e) => handleRowChange(idx, 'material_id', e.target.value)}
                        >
                          <option value="">Select Material...</option>
                          {filteredStockMaterials.map(s => (
                            <option key={s.material_id} value={s.material_id}>
                              {s.material_name} | {s.material_code || s.code || s.material_id} | Avail: {s.available_quantity}{s.unit}
                            </option>
                          ))}
                        </select>
                        <div className="flex justify-between items-center">
                          {row.stock_avail > 0 && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> Stock: {row.stock_avail} {row.unit}</span>}
                          {row.material_id && Number(row.qty || 0) > Number(row.stock_avail || 0) && (
                            <span className="text-[10px] text-rose-600 flex items-center gap-1"><AlertCircle size={10} /> Insufficient for reserved qty</span>
                          )}
                          {row.material_id && row.qty !== '' && (
                            <span className="text-[10px] text-indigo-600">Balance: {Number(row.stock_avail || 0) - Number(row.qty || 0)} {row.unit}</span>
                          )}
                          {row.is_preassigned ? (
                            <span className="text-[10px] text-blue-600 font-medium flex items-center gap-1"><FileText size={8} /> Pre-assigned</span>
                          ) : row.is_assigned ? (
                            <span className="text-[10px] text-blue-500 font-medium flex items-center gap-1"><FileText size={8} /> From Design</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="w-28">
                        <input
                          type="number" placeholder="Qty"
                          step={isPieceUnit(row.unit) ? '1' : '0.001'}
                          min="0"
                          className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white"
                          value={row.qty}
                          onChange={(e) => handleRowChange(idx, 'qty', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => saveMaterialRow(idx)}
                        className={`p-1.5 rounded ${row.is_saved ? 'text-emerald-600 bg-emerald-50' : 'text-indigo-600 hover:bg-indigo-50'}`}
                        title={row.is_saved ? 'Added' : 'Add Material'}
                      >
                        <Save size={14} />
                      </button>
                      <button onClick={() => removeMaterialRow(idx)} className="p-1.5 text-slate-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {materialsToIssue.length === 0 && (
                    <div className="text-center py-4 text-xs text-slate-400 italic">No materials to issue.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {showAssignedStones && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-slate-900">Assigned Stones</h4>
                    <p className="text-xs text-slate-500">Loaded from Design Master for this design.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={addStoneRow} className="text-xs flex items-center gap-1 font-medium text-indigo-600 hover:text-indigo-800">
                      <Plus size={14} /> Add New Stone
                    </button>
                    <button type="button" onClick={loadStoneContext} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                      Reload
                    </button>
                  </div>
                </div>
                {assignedStoneRows.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {assignedStoneRows.map((row, idx) => (
                      <div key={`${row.stone_name}-${idx}`} className="rounded border border-indigo-100 bg-white p-2 text-sm">
                        <div className="font-semibold text-slate-900 break-all">{row.stone_name || 'Stone'}</div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                          <span>Size: <b>{row.size || '-'}</b></span>
                          <span>Qty: <b>{row.count}</b></span>
                          <span>Grade: <b>{row.quality_grade || '-'}</b></span>
                          <span>Clarity: <b>{row.clarity || '-'}</b></span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-xs text-slate-400 italic">No design stones assigned.</div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-slate-900">Stones Used In Production</h4>
                    <p className="text-xs text-slate-500">Edit assigned stones or add extra stones used for this run.</p>
                  </div>
                  <button type="button" onClick={addStoneRow} className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium">
                    <Plus size={14} /> Add Stone
                  </button>
                </div>
                {loadingStones ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-xs text-slate-400">
                    <Loader2 className="animate-spin" size={14} /> Loading assigned stones...
                  </div>
                ) : (
                  <div className="space-y-2 overflow-y-auto max-h-[250px] pr-1">
                    {stonesToIssue.map((row, idx) => (
                      <div key={idx} className="rounded border border-slate-200 bg-white p-2 shadow-sm">
                        <div className="grid gap-2 md:grid-cols-2">
                          <div>
                            <select
                              className="w-full rounded border border-slate-300 p-2 text-sm outline-none bg-white"
                              value={row.stone_name}
                              onChange={(e) => handleStoneSelect(idx, e.target.value)}
                            >
                              <option value="">Select Stone...</option>
                              {inventoryStoneOptions.map((stone) => (
                                <option key={`stone-${stone.material_id || stone.name}-${idx}`} value={stone.name || ''}>
                                  {stone.name} {stone.size ? `| ${stone.size}` : ''} {stone.quality_grade ? `| ${stone.quality_grade}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <input className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Type"
                            value={row.stone_type} onChange={(e) => handleStoneRowChange(idx, 'stone_type', e.target.value)} />
                          <input className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Size"
                            value={row.size} onChange={(e) => handleStoneRowChange(idx, 'size', e.target.value)} />
                          <input type="number" min="1" className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Qty"
                            value={row.count} onChange={(e) => handleStoneRowChange(idx, 'count', e.target.value)} />
                          <input className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Quality Grade"
                            value={row.quality_grade} onChange={(e) => handleStoneRowChange(idx, 'quality_grade', e.target.value)} />
                          <input className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Clarity"
                            value={row.clarity} onChange={(e) => handleStoneRowChange(idx, 'clarity', e.target.value)} />
                          <input className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Cut"
                            value={row.cut} onChange={(e) => handleStoneRowChange(idx, 'cut', e.target.value)} />
                          <input className="w-full rounded border border-slate-300 p-2 text-sm outline-none" placeholder="Color"
                            value={row.color} onChange={(e) => handleStoneRowChange(idx, 'color', e.target.value)} />
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-[10px] text-slate-500">
                            {row.is_assigned ? 'From Design' : 'Manual Stone'}{row.is_saved ? ' • Saved' : ''}
                          </span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => saveStoneRow(idx)}
                              className={`p-1.5 rounded ${row.is_saved ? 'text-emerald-600 bg-emerald-50' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                              <Save size={14} />
                            </button>
                            <button type="button" onClick={() => removeStoneRow(idx)} className="p-1.5 text-slate-400 hover:text-red-500">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {stonesToIssue.length === 0 && <div className="text-center py-4 text-xs text-slate-400 italic">No stones added.</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Remarks</label>
            <textarea
              className="w-full p-2.5 border border-slate-300 rounded-lg outline-none h-16 resize-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Instructions or notes..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 bg-white p-5 sm:rounded-b-2xl">
          <button onClick={onClose} className="rounded-lg px-4 py-2 font-medium text-slate-600 transition-colors hover:bg-slate-100" disabled={loadingSubmit}>
            Cancel
          </button>
          <button
            disabled={!primaryWorkerId || (shouldShowInputWeight && !beforeWeight) || loadingSubmit}
            onClick={handleConfirm}
            className="flex items-center gap-2 rounded-lg bg-[#3B82F6] px-6 py-2 font-medium text-white shadow-sm transition-colors disabled:bg-slate-600"
          >
            {loadingSubmit ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : <><Play size={16} fill="currentColor" /> Start & Issue</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- BULK START MODAL (PRE-CASTING) ---

const PIECES_PER_TREE = 50;

const BulkStartModal = ({ isOpen, onClose, onStart, stageCode, stageName, jobCards: initialJobCards, allJobCards }) => {
  const [primaryWorkerId, setPrimaryWorkerId] = useState('');
  const [additionalWorkers, setAdditionalWorkers] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [beforeWeight, setBeforeWeight] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [workers, setWorkers] = useState([]);
  const [stockMaterials, setStockMaterials] = useState([]);
  const [materialsToIssue, setMaterialsToIssue] = useState([{ material_id: '', qty: '', stock_avail: 0, unit: 'g', is_assigned: false }]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isAllWorkersLoaded, setIsAllWorkersLoaded] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [additionalOrderId, setAdditionalOrderId] = useState('');
  const [additionalOrderJobs, setAdditionalOrderJobs] = useState([]);
  const [startWithInsufficient, setStartWithInsufficient] = useState(false);
  const [materialSearch, setMaterialSearchBulk] = useState('');

  const isTreeMaking = stageCode === 'TREE_MAKING';
  const pendingTreeCandidates = (allJobCards || []).filter((entry) =>
    entry?.stages?.pre_casting?.[0]?.stage_code === 'TREE_MAKING' &&
    entry?.stages?.pre_casting?.[0]?.status === 'PENDING'
  );
  const additionalLookupSuggestions = Array.from(new Set(
    pendingTreeCandidates.flatMap((entry) => [
      entry.order_id ? `Order: ${entry.order_id}` : null,
      entry.job_card_no ? `JC: ${entry.job_card_no}` : null
    ]).filter(Boolean)
  ));

  const formatDateTimeLocal = (date) => {
    const pad = (n) => n < 10 ? '0' + n : n;
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Intentionally keyed to the bulk-job payload rather than helper identities to avoid reload loops in the modal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isOpen && initialJobCards && initialJobCards.length > 0) {
      setSelectedJobIds(initialJobCards.map(j => j.job_card_id));
      setPrimaryWorkerId('');
      setAdditionalWorkers([]);
      setRemarks('');
      setBeforeWeight('');
      setAdditionalOrderId('');
      setAdditionalOrderJobs([]);
      setStartWithInsufficient(false);
      setIsAllWorkersLoaded(false);
      const now = new Date();
      setExpectedEndDate(formatDateTimeLocal(new Date(now.getTime() + 4 * 60 * 60 * 1000)));
      loadBulkWorkers();
      loadBulkMaterials();
    }
  }, [initialJobCards, isOpen]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadBulkWorkers = useCallback(async () => {
    setLoadingWorkers(true);
    const firstJob = initialJobCards && initialJobCards[0];
    if (!firstJob) { setLoadingWorkers(false); return; }
    const stageId = firstJob.stages?.pre_casting?.[0]?.stage_id;
    if (stageId) {
      const data = await api.fetchStageWorkers(stageId);
      setWorkers(dedupeWorkers(data.map((worker) => normalizeWorkerRecord({
        ...worker,
        role: worker?.role || worker?.role_in_stage || '',
      }))));
    }
    setLoadingWorkers(false);
  }, [initialJobCards]);

  const loadBulkMaterials = useCallback(async () => {
    setLoadingMaterials(true);
    try {
      const stock = await api.fetchMaterialsWithStock();
      setStockMaterials(stock);
      const firstJob = initialJobCards && initialJobCards[0];
      if (firstJob) {
        const assigned = await api.fetchAssignedStageMaterials(firstJob.design_code, stageCode);
        if (assigned.length > 0) {
          const rows = dedupeAssignedMaterialRows(
            assigned.map(a => ({
              material_id: a.material_id,
              qty: a.qty,
              unit: a.unit,
              is_assigned: true,
              is_saved: true,
            })),
            stock
          );
          setMaterialsToIssue(rows);
          return;
        }
      }
      setMaterialsToIssue([{ material_id: '', qty: '', stock_avail: 0, unit: 'g', is_assigned: false }]);
    } finally {
      setLoadingMaterials(false);
    }
  }, [initialJobCards, stageCode]);

  const handleWorkerSelectBulk = async (e) => {
    const val = e.target.value;
    if (val === 'LOAD_ALL') {
      setLoadingWorkers(true);
      const all = await api.fetchAllWorkers();
      const normalizedAllWorkers = all.map(normalizeWorkerRecord);
      const existingIds = new Set((workers || []).map((worker) => getWorkerIdentity(worker)));
      const remainingWorkers = normalizedAllWorkers.filter((worker) =>
        !existingIds.has(getWorkerIdentity(worker)) &&
        workerMatchesStageRole(worker, stageCode)
      );
      setWorkers([
        ...dedupeWorkers([
          ...(workers || []).map(normalizeWorkerRecord),
          ...remainingWorkers,
        ]),
      ]);
      setIsAllWorkersLoaded(true);
      setLoadingWorkers(false);
    } else {
      setPrimaryWorkerId(val);
    }
  };

  const handleAddWorkerBulk = () => setAdditionalWorkers([...additionalWorkers, { worker_id: '', role_in_stage: 'Assistant' }]);
  const handleRemoveWorkerBulk = (idx) => setAdditionalWorkers(additionalWorkers.filter((_, i) => i !== idx));
  const handleAdditionalWorkerChangeBulk = (idx, field, value) => {
    const updated = [...additionalWorkers];
    updated[idx][field] = value;
    setAdditionalWorkers(updated);
  };

  const handleRowChangeBulk = (index, field, value) => {
    const updated = [...materialsToIssue];
    updated[index][field] = field === 'qty'
      ? normalizeMaterialQtyInput(value, updated[index]?.unit)
      : value;
    if (field === 'material_id') {
      const match = stockMaterials.find(s => s.material_id.toString() === value.toString());
      updated[index].stock_avail = match ? match.available_quantity : 0;
      updated[index].unit = match ? match.unit : 'g';
      updated[index].qty = normalizeMaterialQtyInput(updated[index].qty, updated[index].unit);
    }
    setMaterialsToIssue(updated);
  };

  const addMaterialRowBulk = () => setMaterialsToIssue([...materialsToIssue, { material_id: '', qty: '', stock_avail: 0, unit: 'g', is_assigned: false }]);
  const removeMaterialRowBulk = (idx) => setMaterialsToIssue(materialsToIssue.filter((_, i) => i !== idx));
  const saveMaterialRowBulk = (idx) => {
    const updated = [...materialsToIssue];
    updated[idx].is_saved = true;
    setMaterialsToIssue(updated);
  };

  const filteredStockMaterialsBulk = stockMaterials.filter((m) => {
    if (!materialSearch.trim()) return true;
    const query = materialSearch.toLowerCase();
    const haystack = [m.material_name, m.material_code, m.code, m.material_id].join(' ').toLowerCase();
    return haystack.includes(query);
  });
  const assignedMaterialRowsBulk = materialsToIssue.filter((row) => row.is_assigned);
  const hasNegativeBalance = materialsToIssue.some(row => row.material_id && row.qty !== '' && (Number(row.stock_avail || 0) - Number(row.qty || 0)) < 0);

  const handleToggleJob = (jobId) => {
    setSelectedJobIds(prev => prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]);
  };

  const handleAddAdditionalOrder = () => {
    if (!additionalOrderId.trim()) return;
    const rawLookup = additionalOrderId.replace(/^order:\s*/i, '').replace(/^jc:\s*/i, '').trim();
    const oid = resolveOrderLookupInput(rawLookup, pendingTreeCandidates);
    const existingJobIds = [...selectedJobIds, ...additionalOrderJobs.map(j => j.job_card_id)];
    const addJobs = (allJobCards || []).filter(j =>
      String(j.order_id || '') === String(oid || '') &&
      j.stages?.pre_casting?.[0]?.status === 'PENDING' &&
      !existingJobIds.includes(j.job_card_id)
    );
    if (addJobs.length === 0) {
      alert(`No startable job cards found for Order ID: ${oid}`);
      return;
    }
    setAdditionalOrderJobs(prev => [...prev, ...addJobs]);
    setAdditionalOrderId('');
  };

  const handleRemoveAdditionalJob = (jobId) => {
    setAdditionalOrderJobs(prev => prev.filter(j => j.job_card_id !== jobId));
  };

  const selectedFromInitial = (initialJobCards || []).filter(j => selectedJobIds.includes(j.job_card_id));
  const allSelectedJobs = useMemo(() => [...selectedFromInitial, ...additionalOrderJobs], [additionalOrderJobs, selectedFromInitial]);
  const totalPieces = allSelectedJobs.reduce((sum, j) => sum + (j.quantity || 0), 0);
  const treeCount = totalPieces > 0 ? Math.ceil(totalPieces / PIECES_PER_TREE) : 0;
  const isInsufficient = isTreeMaking && totalPieces > 0 && totalPieces < PIECES_PER_TREE;
  const canConfirm = primaryWorkerId && allSelectedJobs.length > 0 && (!isInsufficient || startWithInsufficient) && !hasNegativeBalance;

  const handleBulkConfirm = async () => {
    if (!canConfirm) return;
    setLoadingSubmit(true);
    try {
      const validMaterials = materialsToIssue.filter(m => m.material_id && Number(m.qty) > 0);
      const validAdditionalWorkers = additionalWorkers.filter(aw => aw.worker_id);

      // Check if any selected material has 0 or insufficient stock
      const outOfStockMaterials = validMaterials.filter(m => Number(m.stock_avail || 0) <= 0);
      if (outOfStockMaterials.length > 0) {
        const names = outOfStockMaterials.map(m => {
          const match = stockMaterials.find(s => String(s.material_id) === String(m.material_id));
          return match?.material_name || `Material #${m.material_id}`;
        }).join(', ');
        alert(`The issued materials (${names}) are out of stock. Please contact the inventory manager or admin to update the stock data.`);
        setLoadingSubmit(false);
        return;
      }

      const insufficientMaterials = validMaterials.filter(m => Number(m.qty) > Number(m.stock_avail || 0));
      if (insufficientMaterials.length > 0) {
        const details = insufficientMaterials.map(m => {
          const match = stockMaterials.find(s => String(s.material_id) === String(m.material_id));
          return `${match?.material_name || m.material_id}: need ${m.qty}, available ${m.stock_avail || 0}`;
        }).join('\n');
        alert(`Insufficient stock for the following materials:\n${details}\n\nPlease contact the inventory manager or admin to update the stock data.`);
        setLoadingSubmit(false);
        return;
      }

      const stageIds = allSelectedJobs.map(j => j.stages?.pre_casting?.[0]?.stage_id).filter(Boolean);
      await onStart({
        worker_id: primaryWorkerId,
        additional_workers: validAdditionalWorkers,
        expected_end_time: expectedEndDate,
        before_weight: beforeWeight || 0,
        remarks,
        issued_materials: validMaterials,
        stage_ids: stageIds,
        ...(isTreeMaking && { tree_count: treeCount, total_pieces: totalPieces })
      });
      onClose();
    } catch (e) {
      console.error('Bulk start failed', e);
      const errMsg = e?.response?.data?.message || e?.message || "Failed to bulk start stages.";
      alert(errMsg);
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="pre-mobile-modal bg-white rounded-xl shadow-2xl max-w-5xl w-full flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
          <div>
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              <Play size={18} className="text-indigo-600" fill="currentColor" />
              Bulk Start â€” {stageName}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {allSelectedJobs.length} job card{allSelectedJobs.length !== 1 ? 's' : ''} selected
              {isTreeMaking && totalPieces > 0 && (
                <span className="ml-2 text-amber-700 font-bold">| {totalPieces} pieces | {treeCount} tree{treeCount !== 1 ? 's' : ''}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Tree Making Calculator â€” only for TREE_MAKING */}
          {isTreeMaking && (
            <div className={`rounded-xl border p-4 ${isInsufficient ? 'bg-amber-50 border-amber-200' : totalPieces === 0 ? 'bg-slate-50 border-slate-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <h4 className="text-sm font-bold flex items-center gap-2 mb-3">
                <GitMerge size={16} className={isInsufficient ? 'text-amber-600' : 'text-emerald-600'} />
                <span className={isInsufficient ? 'text-amber-800' : 'text-emerald-800'}>Tree Making Calculator</span>
                <span className="text-[10px] bg-white border px-2 py-0.5 rounded-full text-slate-500 font-normal">{PIECES_PER_TREE} pieces per tree</span>
              </h4>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-white rounded-lg border p-3 text-center shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Total Pieces</div>
                  <div className="text-2xl font-bold text-slate-800">{totalPieces}</div>
                </div>
                <div className="bg-white rounded-lg border p-3 text-center shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Min Per Tree</div>
                  <div className="text-2xl font-bold text-slate-500">{PIECES_PER_TREE}</div>
                </div>
                <div className={`rounded-lg border p-3 text-center shadow-sm ${isInsufficient ? 'bg-amber-100 border-amber-300' : totalPieces > 0 ? 'bg-emerald-100 border-emerald-300' : 'bg-white'}`}>
                  <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Trees Needed</div>
                  <div className={`text-2xl font-bold ${isInsufficient ? 'text-amber-700' : 'text-emerald-700'}`}>{treeCount}</div>
                </div>
              </div>

              {isInsufficient && (
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-3 flex items-start gap-2">
                  <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-800">Insufficient pieces â€” space & material will be wasted</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Need {PIECES_PER_TREE - totalPieces} more pieces to fill a complete tree. Add job cards from another order below, or proceed with {totalPieces} pieces.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add pieces by Order ID or Job Card ID..."
                  className="flex-1 p-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  value={additionalOrderId}
                  onChange={e => setAdditionalOrderId(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddAdditionalOrder()}
                  list="tree-bulk-lookup-suggestions"
                />
                <datalist id="tree-bulk-lookup-suggestions">
                  {additionalLookupSuggestions.map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
                <button
                  onClick={handleAddAdditionalOrder}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold flex items-center gap-1"
                >
                  <Plus size={14} /> Add Order
                </button>
              </div>
            </div>
          )}

          {/* Job Cards List */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <ClipboardList size={15} /> {isTreeMaking ? 'Job Cards in Tree Batch' : 'Job Cards to Bulk Start'}
              </h4>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {allSelectedJobs.length} selected Â· {totalPieces} pcs
              </span>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2 bg-slate-50">
              {(initialJobCards || []).map(job => (
                <div
                  key={job.job_card_id}
                  className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors select-none ${selectedJobIds.includes(job.job_card_id) ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 opacity-50'}`}
                  onClick={() => handleToggleJob(job.job_card_id)}
                >
                  <input type="checkbox" checked={selectedJobIds.includes(job.job_card_id)} readOnly className="rounded pointer-events-none" />
                  <span className="font-mono text-xs bg-white border border-slate-200 px-2 py-0.5 rounded">{job.job_card_no}</span>
                  <span className="text-sm font-medium text-slate-700 flex-1">{job.design_code}</span>
                  <span className="text-xs font-bold text-indigo-600">{job.quantity} pcs</span>
                  <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded">Order: {job.order_id}</span>
                </div>
              ))}
              {additionalOrderJobs.map(job => (
                <div key={job.job_card_id} className="flex items-center gap-3 p-2 rounded-lg border bg-amber-50 border-amber-200">
                  <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-bold shrink-0">+Added</span>
                  <span className="font-mono text-xs bg-white border border-slate-200 px-2 py-0.5 rounded">{job.job_card_no}</span>
                  <span className="text-sm font-medium text-slate-700 flex-1">{job.design_code}</span>
                  <span className="text-xs font-bold text-indigo-600">{job.quantity} pcs</span>
                  <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded">Order: {job.order_id}</span>
                  <button onClick={() => handleRemoveAdditionalJob(job.job_card_id)} className="text-red-400 hover:text-red-600 p-1"><X size={13} /></button>
                </div>
              ))}
              {allSelectedJobs.length === 0 && (
                <div className="text-center py-4 text-xs text-slate-400 italic">No job cards selected</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Worker Assignment */}
            <div className="space-y-3">
              <div className="flex justify-between items-center bg-indigo-50 p-2 rounded-lg border border-indigo-100">
                <label className="text-sm font-bold text-indigo-900 flex items-center gap-2">Assign Workers <span className="text-red-500">*</span></label>
                <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full font-medium">
                  {1 + additionalWorkers.filter(w => w.worker_id).length} assigned
                </span>
              </div>

              <div className="flex gap-2 items-center">
                <div className="w-20 shrink-0 text-xs font-bold text-slate-500 text-right">Primary:</div>
                <div className="relative flex-1">
                  <select
                    className="w-full p-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none bg-white appearance-none"
                    value={primaryWorkerId}
                    onChange={handleWorkerSelectBulk}
                    disabled={loadingSubmit || loadingWorkers}
                  >
                    <option value="">-- Select Staff --</option>
                    {workers.map(w => (
                      <option key={w.id || w.worker_id} value={w.id || w.worker_id}>
                        {formatWorkerOptionLabel(w)}
                      </option>
                    ))}
                    {!isAllWorkersLoaded && <option value="LOAD_ALL" className="font-bold text-indigo-600">+ Show All Workers</option>}
                  </select>
                  <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={14} />
                </div>
              </div>

              {additionalWorkers.map((aw, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <div className="w-20 shrink-0">
                    <select
                      className="w-full p-2 text-xs border border-slate-300 rounded outline-none bg-slate-50 text-slate-700 font-medium"
                      value={aw.role_in_stage}
                      onChange={e => handleAdditionalWorkerChangeBulk(idx, 'role_in_stage', e.target.value)}
                    >
                      <option value="Assistant">Assistant</option>
                      <option value="Trainee">Trainee</option>
                      <option value="Co-Worker">Co-Worker</option>
                    </select>
                  </div>
                  <div className="relative flex-1">
                    <select
                      className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white"
                      value={aw.worker_id}
                      onChange={e => handleAdditionalWorkerChangeBulk(idx, 'worker_id', e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {workers.map(w => (
                        <option key={w.id || w.worker_id} value={w.id || w.worker_id}
                          disabled={w.id === Number(primaryWorkerId) || w.worker_id === Number(primaryWorkerId)}>
                          {formatWorkerOptionLabel(w)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button onClick={() => handleRemoveWorkerBulk(idx)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                </div>
              ))}

              <div className="flex justify-end">
                <button onClick={handleAddWorkerBulk} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-indigo-50 px-3 py-1.5 rounded-full">
                  <Plus size={12} /> Add another worker
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Input Weight (g)</label>
                <input type="number" step="0.01" className="w-full p-2.5 border border-slate-300 rounded-lg outline-none bg-white"
                  placeholder="0.00" value={beforeWeight} onChange={e => setBeforeWeight(normalizeNumericInput(e.target.value))} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Expected Delivery</label>
                <input type="datetime-local" className="w-full p-2.5 border border-slate-300 rounded-lg outline-none bg-white text-sm"
                  value={expectedEndDate} onChange={e => setExpectedEndDate(e.target.value)} />
              </div>
            </div>

            {/* Materials - same for all job cards (matching single-stage format) */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-col h-full min-h-[250px]">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Package size={16} /> Issue Materials
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold border border-blue-200">Same for all</span>
                </label>
                <button onClick={addMaterialRowBulk} className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium">
                  <Plus size={14} /> Add Row
                </button>
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                <input
                  type="text"
                  list="pre-bulk-material-search-suggestions"
                  value={materialSearch}
                  onChange={(e) => setMaterialSearchBulk(e.target.value)}
                  placeholder="Search material name / code"
                  className="w-full rounded border border-slate-300 bg-white pl-8 pr-2 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <datalist id="pre-bulk-material-search-suggestions">
                  {stockMaterials.slice(0, 150).map((m) => (
                    <option key={`bms-${m.material_id}`} value={m.material_name || ''}>
                      {m.material_code || m.code || m.material_id}
                    </option>
                  ))}
                </datalist>
              </div>
              {assignedMaterialRowsBulk.length > 0 && (
                <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-700">Assigned Design Materials</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {assignedMaterialRowsBulk.map((row, idx) => {
                      const material = stockMaterials.find((m) => String(m.material_id) === String(row.material_id));
                      return (
                        <div key={`${row.material_id}-${idx}`} className="rounded border border-indigo-100 bg-white p-2 text-sm">
                          <div className="font-semibold text-slate-900 break-all">{material?.material_name || row.material_id}</div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                            <span>Code: <b>{material?.material_code || material?.code || row.material_id}</b></span>
                            <span>Assigned Qty: <b>{row.qty} {row.unit}</b></span>
                            <span>Stock: <b>{row.stock_avail || 0} {row.unit}</b></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {loadingMaterials ? (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-xs gap-2">
                  <Loader2 className="animate-spin" size={14} /> Checking Inventory...
                </div>
              ) : (
                <div className="space-y-2 overflow-y-auto max-h-[250px] pr-1">
                  {materialsToIssue.map((row, idx) => {
                    const balance = Number(row.stock_avail || 0) - Number(row.qty || 0);
                    const isNegative = row.material_id && row.qty !== '' && balance < 0;
                    return (
                      <div key={idx} className={`flex gap-2 items-start bg-white p-2 rounded border shadow-sm ${isNegative ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}>
                        <div className="flex-1 space-y-1">
                          <select
                            className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white"
                            value={row.material_id}
                            onChange={(e) => handleRowChangeBulk(idx, 'material_id', e.target.value)}
                          >
                            <option value="">Select Material...</option>
                            {filteredStockMaterialsBulk.map(s => (
                              <option key={s.material_id} value={s.material_id}>
                                {s.material_name} | {s.material_code || s.code || s.material_id} | Avail: {s.available_quantity}{s.unit}
                              </option>
                            ))}
                          </select>
                          <div className="flex justify-between items-center">
                            {row.stock_avail > 0 && <span className="text-[10px] text-emerald-600">Stock: {row.stock_avail} {row.unit}</span>}
                            {row.material_id && row.qty !== '' && (
                              <span className={`text-[10px] ${isNegative ? 'text-red-600 font-bold' : 'text-indigo-600'}`}>
                                Remaining: {balance} {row.unit}{isNegative ? ' ⚠ Insufficient' : ''}
                              </span>
                            )}
                            {row.is_assigned && <span className="text-[10px] text-blue-500 font-medium flex items-center gap-1"><FileText size={8} /> From Design</span>}
                          </div>
                        </div>
                        <div className="w-28">
                          <input
                            type="number" placeholder="Qty"
                            step={isPieceUnit(row.unit) ? '1' : '0.001'}
                            min="0"
                            className="w-full p-2 text-sm border border-slate-300 rounded outline-none bg-white"
                            value={row.qty}
                            onChange={(e) => handleRowChangeBulk(idx, 'qty', e.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => saveMaterialRowBulk(idx)}
                          className={`p-1.5 rounded ${row.is_saved ? 'text-emerald-600 bg-emerald-50' : 'text-indigo-600 hover:bg-indigo-50'}`}
                          title={row.is_saved ? 'Added' : 'Add Material'}
                        >
                          <Save size={14} />
                        </button>
                        <button onClick={() => removeMaterialRowBulk(idx)} className="p-1.5 text-slate-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                  {materialsToIssue.length === 0 && (
                    <div className="text-center py-4 text-xs text-slate-400 italic">No materials to issue.</div>
                  )}
                </div>
              )}
              {hasNegativeBalance && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 font-medium">
                  One or more materials have insufficient stock. Adjust quantities to proceed.
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Remarks</label>
            <textarea className="w-full p-2.5 border border-slate-300 rounded-lg outline-none h-14 resize-none"
              placeholder="Notes for all job cards..." value={remarks} onChange={e => setRemarks(e.target.value)} />
          </div>

          {isTreeMaking && isInsufficient && (
            <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
              <input type="checkbox" checked={startWithInsufficient} onChange={e => setStartWithInsufficient(e.target.checked)} className="w-4 h-4 rounded" />
              <span className="text-sm font-medium text-amber-800">
                I understand space & material will be wasted â€” proceed with {totalPieces} pieces anyway
              </span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-100 flex justify-between items-center bg-slate-50 rounded-b-xl">
          <div className="text-xs text-slate-500">
            {isTreeMaking && totalPieces > 0 && (
              <span className={`font-bold ${isInsufficient ? 'text-amber-600' : 'text-emerald-600'}`}>
                {treeCount} tree{treeCount !== 1 ? 's' : ''} Ã— {PIECES_PER_TREE} pieces/tree
                {isInsufficient && ` (${PIECES_PER_TREE - totalPieces} short)`}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} disabled={loadingSubmit} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg font-medium">Cancel</button>
            <button
              disabled={!canConfirm || loadingSubmit}
              onClick={handleBulkConfirm}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-lg font-medium shadow-sm flex items-center gap-2"
            >
              {loadingSubmit ? 'Starting...' : <><Play size={16} fill="currentColor" /> Bulk Start ({allSelectedJobs.length})</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- PRE-CASTING DASHBOARD ---

const PreCastingDashboard = ({ jobCards, delayedStages = [], onStartStage, onExecute, stageFilter, screenTitle, ScreenIcon }) => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('job_card_no') || '');
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkStartModalOpen, setBulkStartModalOpen] = useState(false);
  const [bulkStartJobsOverride, setBulkStartJobsOverride] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [groupByOrder, setGroupByOrder] = useState(false);
  const [orderIdFilter, setOrderIdFilter] = useState(() => searchParams.get('order_id') || '');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    const paramOrderId = searchParams.get('order_id');
    setOrderIdFilter(paramOrderId || '');
    setSearchQuery(searchParams.get('job_card_no') || '');
  }, [searchParams]);

  if (stageFilter === 'DELAYED_STAGES') {
    return (
      <div className="flex h-full flex-col bg-white text-slate-900">
        <div className="border-b border-slate-200 bg-white px-8 py-6">
          <h1 className="flex items-center gap-3 text-2xl font-bold text-slate-900">
            {ScreenIcon ? <ScreenIcon className="text-red-500" /> : <AlertTriangle className="text-red-500" />}
            {screenTitle}
          </h1>
          <p className="mt-2 text-sm text-slate-500">Active stages that have crossed their planned deadline.</p>
        </div>
        <div className="flex-1 overflow-y-auto p-8">
          <DelayedStagesPanel delayedStages={delayedStages} />
        </div>
      </div>
    );
  }

  const uniqueOrders = [...new Set(jobCards.filter((job) => getStageRecordForCode(job, stageFilter)).map(j => j.order_id).filter(Boolean))].sort();

  const activeJobs = jobCards.filter(job => {
    const activeStage = getStageRecordForCode(job, stageFilter);
    if (!activeStage) return false;

    if (orderIdFilter && job.order_id !== orderIdFilter) return false;
    if (statusFilter === 'BULK_STARTED') {
      if (!activeStage.tree_batch_id) return false;
    } else if (statusFilter !== 'ALL' && getStageStatusBucket(activeStage.status) !== statusFilter) {
      return false;
    }

    const query = searchQuery.toLowerCase();
    return (
      (job.job_card_no || '').toLowerCase().includes(query) ||
      (job.design_code || '').toLowerCase().includes(query) ||
      (job.order_id || '').toLowerCase().includes(query)
    );
  }).sort((leftJob, rightJob) => {
    const leftStage = getStageRecordForCode(leftJob, stageFilter);
    const rightStage = getStageRecordForCode(rightJob, stageFilter);
    const leftPriority = STAGE_STATUS_PRIORITY[getStageStatusBucket(leftStage?.status)] ?? 99;
    const rightPriority = STAGE_STATUS_PRIORITY[getStageStatusBucket(rightStage?.status)] ?? 99;
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    const leftTime = new Date(leftStage?.started_at || leftStage?.completed_at || 0).getTime();
    const rightTime = new Date(rightStage?.started_at || rightStage?.completed_at || 0).getTime();
    if (leftPriority === STAGE_STATUS_PRIORITY.QC_APPROVED) return rightTime - leftTime;
    return leftTime - rightTime;
  });
  const bulkStartableJobs = activeJobs.filter((job) => getStageRecordForCode(job, stageFilter)?.status === 'PENDING');
  const isOrderFocusedView = Boolean(orderIdFilter);

  const handleOpenStartModal = (job, stage) => {
    setSelectedJob(job);
    setSelectedStage(stage);
    setModalOpen(true);
  };

  const handleConfirmStart = async (data) => {
    const resolvedStageId = getStageId(selectedStage);
    if (!resolvedStageId) {
      alert('Unable to start this stage because its stage ID is missing. Please refresh the page and try again.');
      return;
    }
    await onStartStage(resolvedStageId, data);
    setModalOpen(false);
  };

  const handleBulkConfirmStart = async (data) => {
    await onStartStage(null, data);
  };

  const handleStartAllInOrder = (orderId) => {
    const orderPendingJobs = activeJobs.filter(j =>
      j.order_id === orderId && getStageRecordForCode(j, stageFilter)?.status === 'PENDING'
    );
    setBulkStartJobsOverride(orderPendingJobs);
    setBulkStartModalOpen(true);
  };

  return (
    <div className="flex flex-col h-full bg-white text-slate-900">
      <StartStageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onStart={handleConfirmStart}
        stage={selectedStage}
        job={selectedJob}
        allJobCards={jobCards}
      />
      <BulkStartModal
        isOpen={bulkStartModalOpen}
        onClose={() => { setBulkStartModalOpen(false); setBulkStartJobsOverride(null); }}
        onStart={handleBulkConfirmStart}
        stageCode={stageFilter}
        stageName={screenTitle}
        jobCards={bulkStartJobsOverride || bulkStartableJobs}
        allJobCards={jobCards}
      />

      <div className="px-8 py-6 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
              {ScreenIcon ? <ScreenIcon className="text-[#3B82F6]" /> : <Hammer className="text-[#3B82F6]" />}
              {screenTitle}
            </h1>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="relative lg:col-span-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search Job No, Order ID, Design..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-[#3B82F6] bg-white text-slate-900"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="relative lg:col-span-3">
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Order ID</label>
            <Filter className="absolute left-3 top-[34px] -translate-y-1/2 text-slate-400" size={16} />
            <ChevronDown className="pointer-events-none absolute right-3 top-[34px] -translate-y-1/2 text-slate-400" size={16} />
            <select
              className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-slate-900 outline-none focus:ring-2 focus:ring-[#3B82F6] font-medium"
              value={orderIdFilter}
              onChange={(e) => setOrderIdFilter(e.target.value)}
            >
              <option value="">All Orders</option>
              {uniqueOrders.map(oid => (
                <option key={oid} value={oid}>{oid}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => !isOrderFocusedView && setGroupByOrder(!groupByOrder)}
            disabled={isOrderFocusedView}
            className={`mt-[18px] px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2 border transition-colors lg:col-span-3 ${groupByOrder && !isOrderFocusedView ? 'bg-[#3B82F6] text-white border-[#3B82F6] shadow' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'} ${isOrderFocusedView ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Layers size={18} /> Group by Order ID
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {STAGE_STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setStatusFilter(filter.key)}
              className={`px-3 py-2 rounded-lg border text-sm font-bold transition-colors ${
                statusFilter === filter.key
                  ? 'bg-[#3B82F6] text-white border-[#3B82F6]'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status Legend */}
      <div className="px-8 py-3 border-b border-slate-200 bg-white flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span className="font-bold text-slate-400 uppercase tracking-wide mr-1">Legend:</span>
        <span className="flex items-center gap-1.5"><Lock size={12} className="text-slate-400" /><span className="text-slate-400">Locked</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#f4b740] inline-block"></span><span className="text-[#f4b740] font-medium">Ready to Start</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6] inline-block animate-pulse"></span><span className="text-blue-600 font-medium">In Progress</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block"></span><span className="text-orange-600 font-medium">Awaiting QC</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#2ecc71] inline-block"></span><span className="text-emerald-700 font-medium">QC Approved</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#ef4444] inline-block"></span><span className="text-rose-600 font-medium">Rework Required</span></span>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {isOrderFocusedView && (
          <div className="sticky top-0 z-20 mb-4 flex items-center justify-between rounded-xl border border-indigo-100 bg-white/95 px-4 py-3 backdrop-blur">
            <div className="text-sm font-semibold text-slate-700">
              Order ID: <span className="text-indigo-700">{orderIdFilter}</span> • {activeJobs.length} job cards
            </div>
            <button
              onClick={() => { setBulkStartJobsOverride(null); setBulkStartModalOpen(true); }}
              disabled={bulkStartableJobs.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Play size={14} fill="currentColor" /> Start All ({bulkStartableJobs.length})
            </button>
          </div>
        )}
        {activeJobs.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No tasks found for this stage.</p>
          </div>
        ) : (
          <div className={isOrderFocusedView ? 'space-y-4' : (groupByOrder ? 'space-y-4' : 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3')}>
            {(() => {
              const renderJobRow = (job) => {
                const activeStage = getStageRecordForCode(job, stageFilter);
                if (!activeStage) return null;
                const normalizedStatus = getDisplayStageStatus(activeStage, job.stage_statuses || []);
                const stageMeta = STAGE_STATUS_META[normalizedStatus] || STAGE_STATUS_META.LOCKED;
                let isDelayed = false;
                const isLocked = normalizedStatus === 'LOCKED';
                const showStartBtn = normalizedStatus === 'PENDING';
                const showExecuteBtn = normalizedStatus === 'IN_PROGRESS';
                const showViewBtn = ['QC_APPROVED', 'COMPLETED'].includes(normalizedStatus);

                return (
                  <div key={job.job_card_id} className={`relative flex h-full flex-col justify-between rounded-2xl border p-5 shadow-sm transition-shadow ${stageMeta.tone} ${isLocked ? 'opacity-85' : 'hover:shadow-lg'}`}>
                    {/* Locked overlay */}
                    {isLocked && (
                      <div className="absolute inset-0 z-10 flex items-start justify-end rounded-2xl p-4 pointer-events-none">
                        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
                          <Lock size={14} className="text-slate-400" />
                          <span>{activeStage.blocking_message || `Awaiting QC Approval of ${activeStage.previous_stage_name || 'previous stage'}`}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-4">
                      <div className={`flex h-16 w-16 flex-col items-center justify-center rounded-xl border font-bold ${isLocked ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-slate-200 bg-slate-100 text-slate-900'}`}>
                        <span className="text-xs uppercase">Job</span>
                        <span className="text-xl font-bold">{job.job_card_id}</span>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg text-slate-900">{job.design_code}</h3>
                          <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">{job.job_card_no}</span>
                          <span className="flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-blue-600">
                            <Package size={12} /> Qty: {job.quantity}
                          </span>
                        </div>

                        {!groupByOrder && (
                          <div className="flex items-center gap-4 mb-2">
                            <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                              Order ID: {job.order_id ?? 'N/A'}
                            </span>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
                          {activeStage.worker_id ? (
                            <span className="flex items-center gap-1 text-emerald-600 font-medium">
                              <User size={14} />
                              {activeStage.worker_count > 1
                                ? `Workers: ${activeStage.worker_name} (Primary), +${activeStage.worker_count - 1} more`
                                : activeStage.worker_name}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-slate-400">
                              <User size={14} /> Unassigned
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Layers size={14} /> {activeStage.stage_name}
                          </span>
                          {activeStage.started_at && (
                            <span className="flex items-center gap-1 text-xs text-slate-400">
                              <Clock size={12} /> {new Date(activeStage.started_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="text-xs font-bold uppercase text-slate-400">Stage Status</div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 font-semibold text-slate-900">
                          {normalizedStatus === 'PENDING' && (
                            <span className="flex items-center gap-1 rounded border border-yellow-500/40 bg-yellow-500/10 px-2 py-0.5 text-xs font-bold text-[#f4b740]">
                              Ready to Start
                            </span>
                          )}
                          {normalizedStatus === 'IN_PROGRESS' && (
                            <span className="flex items-center gap-1 rounded border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-xs font-bold text-blue-600">
                              <span className="h-2 w-2 rounded-full bg-[#3B82F6] animate-pulse" /> {activeStage.worker_name || 'Worker assigned'}
                            </span>
                          )}
                          {normalizedStatus === 'QC_PENDING' && (
                            <span className="flex items-center gap-1 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-xs font-bold text-orange-600">
                              <Clock size={10} /> Awaiting QC
                            </span>
                          )}
                          {normalizedStatus === 'QC_APPROVED' && (
                            <span className="flex items-center gap-1 rounded border border-green-500/40 bg-green-500/10 px-2 py-0.5 text-xs font-bold text-emerald-700">
                              <CheckCircle2 size={10} /> QC Approved
                            </span>
                          )}
                          {normalizedStatus === 'QC_APPROVED' && getOpenReworkPieces(activeStage) > 0 && (
                            <span className="flex items-center gap-1 rounded border border-orange-300 bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-700">
                              {getOpenReworkPieces(activeStage)} rework
                            </span>
                          )}
                          {normalizedStatus === 'QC_REJECTED' && (
                            <span className="flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-xs font-bold text-rose-600">
                              Rework Required
                            </span>
                          )}
                          {activeStage.tree_batch_id && (
                            <span className="flex items-center gap-1 rounded border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-xs font-bold text-indigo-700">
                              <GitMerge size={10} /> Bulk Tree
                            </span>
                          )}
                          <StatusBadge status={normalizedStatus} isDelayed={isDelayed} />
                        </div>
                      </div>

                      {showStartBtn && (
                        <button
                          onClick={() => handleOpenStartModal(job, activeStage)}
                          className="flex items-center gap-2 rounded-lg bg-[#3B82F6] px-5 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-blue-500"
                        >
                          <Play size={16} fill="currentColor" /> Start
                        </button>
                      )}

                      {isLocked && (
                         <div className="flex cursor-not-allowed items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-400">
                          <Lock size={16} className="text-slate-600" /> Locked
                        </div>
                      )}

                      {showExecuteBtn && (
                        <button
                          onClick={() => onExecute(job, activeStage)}
                          className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-blue-500"
                        >
                          <Settings size={16} /> Execute / QC
                        </button>
                      )}

                      {showViewBtn && (
                         <div className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-emerald-700">
                          <CheckCircle2 size={16} className="text-[#2ecc71]" /> Done
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              if (!groupByOrder || isOrderFocusedView) {
                return <>{activeJobs.map(renderJobRow)}</>;
              }

              const grouped = {};
              activeJobs.forEach(job => {
                const oid = job.order_id || 'UNASSIGNED';
                if (!grouped[oid]) grouped[oid] = { order_id: oid, jobs: [], total_qty: 0 };
                grouped[oid].jobs.push(job);
                grouped[oid].total_qty += Number(job.quantity || 0);
              });

              return (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {Object.values(grouped).map(group => {
                    const allPending = group.jobs.every(j => getStageRecordForCode(j, stageFilter)?.status === 'PENDING');
                    return (
                    <div key={group.order_id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-5 py-4 border-b border-slate-200">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <div className="text-[11px] uppercase font-bold tracking-wide text-blue-700">Order ID</div>
                            <div className="text-xl font-extrabold text-slate-900 mt-0.5">{group.order_id === 'UNASSIGNED' ? 'No Order ID' : group.order_id}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
                            <span className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                              {group.jobs.length} Job Cards
                            </span>
                            <span className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                              Qty {group.total_qty}
                            </span>
                            {allPending && group.order_id !== 'UNASSIGNED' && (
                              <button
                                onClick={() => handleStartAllInOrder(group.order_id)}
                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#3B82F6] px-4 py-2 text-sm font-bold text-white hover:bg-blue-500"
                              >
                                <Play size={14} fill="currentColor" /> Start All
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        {group.jobs.map(renderJobRow)}
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div >
  );
};

// --- RETURN MATERIALS MODAL ---
// eslint-disable-next-line no-unused-vars
const ReturnMaterialsModal = ({ isOpen, onClose, stage, job, onSuccess }) => {
  const [materials, setMaterials] = useState([]);
  const [returns, setReturns] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const currentStageId = getStageId(stage);

  useEffect(() => {
    if (isOpen && currentStageId) {
      setLoading(true);
      api.fetchStageMaterials(currentStageId).then(data => {
        setMaterials(data);
        const init = {};
        data.forEach(m => { init[m.material_id] = ''; });
        setReturns(init);
        setLoading(false);
      });
    }
  }, [isOpen, currentStageId]);

  const handleSubmit = async () => {
    const returnList = materials
      .filter(m => parseFloat(returns[m.material_id] || 0) > 0)
      .map(m => ({
        material_id: m.material_id,
        qty_returning: parseFloat(returns[m.material_id])
      }));

    if (returnList.length === 0) { onClose(); return; }

    setSubmitting(true);
    try {
      const res = await api.returnStageMaterials(currentStageId, returnList);
      if (res.success) {
        onSuccess();
        onClose();
      } else {
        alert(res.message || 'Return failed');
      }
    } catch (e) {
      alert('Failed to return materials. See console.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="pre-mobile-modal bg-white rounded-xl shadow-2xl max-w-lg w-full flex flex-col max-h-[85vh]">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
          <div>
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              <Package size={18} className="text-rose-600" /> Return Unused Materials
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {stage?.stage_name} — {job?.job_card_no}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
              <Loader2 size={18} className="animate-spin" /> Loading materials...
            </div>
          ) : materials.length === 0 ? (
            <p className="text-center text-slate-400 py-10 italic">No materials were issued for this stage.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
                Enter the quantity you are returning to inventory for each material. Only unaccounted quantities can be returned.
              </p>
              {materials.map(m => {
                const maxReturn = Math.max(0, parseFloat(m.qty_issued || 0) - parseFloat(m.qty_returned || 0));
                const returningVal = parseFloat(returns[m.material_id] || 0);
                const isOverMax = returningVal > maxReturn;
                return (
                  <div key={m.material_id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-slate-800 text-sm">{m.material_name}</span>
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded font-mono">{m.unit}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-slate-500 mb-3 bg-white p-2 rounded border">
                      <div className="text-center">
                        <div className="font-bold text-slate-400 uppercase text-[10px]">Issued</div>
                        <div className="font-bold text-slate-700">{parseFloat(m.qty_issued || 0).toFixed(3)}</div>
                      </div>
                      <div className="text-center border-x">
                        <div className="font-bold text-slate-400 uppercase text-[10px]">Used</div>
                        <div className="font-bold text-slate-700">{parseFloat(m.qty_used || 0).toFixed(3)}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-slate-400 uppercase text-[10px]">Prev. Returned</div>
                        <div className="font-bold text-emerald-600">{parseFloat(m.qty_returned || 0).toFixed(3)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-bold text-slate-600 w-28 shrink-0">
                        Return Qty <span className="text-rose-500">*</span>
                      </label>
                      <div className="flex-1">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          max={maxReturn}
                          placeholder={`Max returnable: ${maxReturn.toFixed(3)}`}
                          className={`w-full p-2 border rounded-lg text-sm outline-none focus:ring-2 ${isOverMax ? 'border-red-400 focus:ring-red-400' : 'border-slate-300 focus:ring-rose-500'}`}
                          value={returns[m.material_id] || ''}
                          onChange={e => setReturns({ ...returns, [m.material_id]: e.target.value })}
                          disabled={maxReturn === 0}
                        />
                        {isOverMax && (
                          <p className="text-[10px] text-red-500 mt-1">Cannot exceed {maxReturn.toFixed(3)} {m.unit}</p>
                        )}
                        {maxReturn === 0 && (
                          <p className="text-[10px] text-slate-400 mt-1">All material accounted for.</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg font-medium" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || loading || materials.length === 0}
            className="px-6 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white rounded-lg font-medium shadow-sm flex items-center gap-2"
          >
            {submitting ? 'Returning...' : <><CheckCircle2 size={16} /> Confirm Return</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- EXECUTION SCREEN ---

const PreCastingExecutionScreen = ({ jobCard, stage, onBack, onSave, stageFlow = [] }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [completeData, setCompleteData] = useState({
    after_weight: '',
    pieces_count: '',
    remarks: ''
  });
  const [stageInputs, setStageInputs] = useState({});
  const [stageWorkers, setStageWorkers] = useState([]);
  const [contributions, setContributions] = useState({});
  const [completionResult, setCompletionResult] = useState(null);
  const [treeBatchStages, setTreeBatchStages] = useState([]);
  const [treePieceDistribution, setTreePieceDistribution] = useState([]);
  const [loadingTreeBatch, setLoadingTreeBatch] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const isCompleted = ['COMPLETED', 'QC_PENDING', 'QC_APPROVED', 'CPMPLETED'].includes(stage.status);

  // Photos
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [issuedMaterials, setIssuedMaterials] = useState([]);
  const [issuedStones, setIssuedStones] = useState([]);
  const [materialConsumptionDraft, setMaterialConsumptionDraft] = useState({});
  const stageId = getStageId(stage);
  const stageCodeNormalized = normalizeStageCode(stage.stage_code || stage.stage_name);
  const totalIssuedStonePieces = issuedStones.reduce((sum, row) => sum + Number(row.count || row.qty_issued || 0), 0);
  const isTreeMakingStage = stageCodeNormalized === 'TREE_MAKING';
  const incomingPieces = Number(
    stage?.pieces_in ??
    prefillData?.pieces_in ??
    stage?.pre_fill_data?.pieces_in ??
    jobCard?.quantity ??
    0
  );

  const handlePhotoChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'before') {
          setBeforePhoto(file);
          setBeforePreview(reader.result);
        } else {
          setAfterPhoto(file);
          setAfterPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });

  useEffect(() => {
    if (stage.worker_count > 1) {
      api.fetchAssignedStageWorkers(stageId).then(workers => {
        setStageWorkers(workers);
        const initCont = {};
        workers.forEach(w => {
          initCont[w.worker_id] = { hours: '', pieces: '' };
        });
        setContributions(initCont);
      });
    }
  }, [stage.worker_count, stageId]);

  useEffect(() => {
    const defaultPieces =
      stage?.pieces_in ??
      stage?.pre_fill_data?.pieces_in ??
      jobCard?.quantity ??
      '';
    setCompleteData({
      after_weight: stage?.before_weight ? String(stage.before_weight) : '',
      pieces_count: defaultPieces !== '' ? String(defaultPieces) : '',
      remarks: ''
    });
  }, [jobCard?.quantity, stage?.before_weight, stage?.pieces_in, stage?.pre_fill_data?.pieces_in, stageId]);

  useEffect(() => {
    let isMounted = true;
    if (!stageId) return;

    (async () => {
      const [prefill, materials] = await Promise.all([
        api.fetchStagePrefill(stageId),
        api.fetchStageMaterials(stageId)
      ]);
      if (!isMounted) return;

      setPrefillData(prefill?.pre_fill_data || prefill || null);
      setIssuedMaterials(
        (Array.isArray(materials) ? materials : []).filter((row) =>
          Number(row.qty_issued || 0) > 0 ||
          Number(row.qty_used || 0) > 0 ||
          Number(row.qty_returned || 0) > 0 ||
          Number(row.qty_wastage || 0) > 0
        )
      );
      const nextIssuedStones = stage?.process_data?.issued_stones || stage?.issued_stones || [];
      setIssuedStones(
        Array.isArray(nextIssuedStones)
          ? nextIssuedStones
            : []
      );

      const prefillPieces = prefill?.pieces_in ?? '';
      if (prefillPieces !== '' && prefillPieces !== null && prefillPieces !== undefined) {
        setCompleteData((prev) => ({
          ...prev,
          pieces_count: prev.pieces_count ? prev.pieces_count : String(prefillPieces)
        }));
      }
    })();

    return () => { isMounted = false; };
  }, [stageId, stage?.issued_stones, stage?.process_data]);

  useEffect(() => {
    let isMounted = true;
    const loadTreeBatchStages = async () => {
      if (!isTreeMakingStage || !stage?.tree_batch_id) {
        setTreeBatchStages([]);
        setTreePieceDistribution([]);
        return;
      }
      setLoadingTreeBatch(true);
      const rows = await api.fetchTreeBatchStages(stage.tree_batch_id);
      if (!isMounted) return;
      const normalizedRows = Array.isArray(rows) ? rows : [];
      setTreeBatchStages(normalizedRows);
      const distribution = normalizedRows.map((row) => ({
        stage_id: row.stage_id,
        job_card_id: row.job_card_id,
        job_card_no: row.job_card_no,
        pieces: Number(row.pieces_in || row.quantity || 0),
      }));
      setTreePieceDistribution(distribution);
      setLoadingTreeBatch(false);
    };
    loadTreeBatchStages();
    return () => { isMounted = false; };
  }, [isTreeMakingStage, stage?.tree_batch_id]);

  useEffect(() => {
    if (!isTreeMakingStage || treePieceDistribution.length === 0) return;
    const totalPieces = treePieceDistribution.reduce((sum, row) => sum + (Number(row.pieces) || 0), 0);
    setCompleteData((prev) => ({
      ...prev,
      pieces_count: String(totalPieces),
    }));
  }, [isTreeMakingStage, treePieceDistribution]);

  const getEffectiveUsedQty = (row) => {
    const used = parseFloat(row.qty_effective_used ?? row.qty_used);
    if (Number.isFinite(used) && used > 0) return used;
    const issued = parseFloat(row.qty_issued) || 0;
    const returned = parseFloat(row.qty_returned) || 0;
    const wastage = parseFloat(row.qty_wastage) || 0;
    return Math.max(issued - returned - wastage, 0);
  };

  useEffect(() => {
    const nextDraft = {};
    (issuedMaterials || []).forEach((row) => {
      const issued = parseFloat(row.qty_issued || 0);
      const returned = parseFloat(row.qty_returned || 0);
      const wastage = parseFloat(row.qty_wastage || 0);
      const existingUsed = parseFloat(row.qty_used || 0);
      const defaultUsed = Math.max(existingUsed > 0 ? existingUsed : (issued - returned - wastage), 0);
      nextDraft[row.material_id] = {
        use_full_qty: returned <= 0,
        qty_used: defaultUsed.toFixed(3)
      };
    });
    setMaterialConsumptionDraft(nextDraft);
  }, [stageId, issuedMaterials]);

  useEffect(() => {
    if (stageCodeNormalized !== 'WAX_SETTING' || totalIssuedStonePieces <= 0 || isCompleted) return;
    setStageInputs((prev) => ({
      ...prev,
      stones_issued: String(totalIssuedStonePieces),
    }));
  }, [isCompleted, stageCodeNormalized, totalIssuedStonePieces]);

  const updateMaterialConsumptionDraft = (materialId, patch) => {
    setMaterialConsumptionDraft((prev) => ({
      ...prev,
      [materialId]: {
        ...(prev[materialId] || {}),
        ...patch,
      }
    }));
  };

  const getMaterialDraftValues = (row) => {
    const issued = parseFloat(row.qty_issued || 0);
    const wastage = parseFloat(row.qty_wastage || 0);
    const draft = materialConsumptionDraft[row.material_id] || {};
    const useFullQty = draft.use_full_qty !== false;
    let qtyUsed = useFullQty ? Math.max(issued - wastage, 0) : parseFloat(draft.qty_used || 0);
    if (!Number.isFinite(qtyUsed) || qtyUsed < 0) qtyUsed = 0;
    if (qtyUsed > issued) qtyUsed = issued;
    const qtyLeft = Math.max(issued - qtyUsed - wastage, 0);
    return { useFullQty, qtyUsed, qtyLeft, issued, wastage };
  };

  const isNoBeforeWeightStage = ['WAX_CREATION', 'WAX_CLEANING'].includes(
    normalizeStageCode(stage.stage_code || stage.stage_name)
  );
  const numericInputWeight = isNoBeforeWeightStage ? 0 : (parseFloat(stage.before_weight) || 0);
  const materialWeightUsed = issuedMaterials.reduce((sum, row) => sum + getMaterialDraftValues(row).qtyUsed, 0);
  const expectedWeight = numericInputWeight + materialWeightUsed;
  const numericAfterWeight = parseFloat(completeData.after_weight) || 0;
  const netLossGainWeight = isNoBeforeWeightStage ? 0 : (expectedWeight - numericAfterWeight);
  const wastageWeight = Math.max(netLossGainWeight, 0);
  const metalLossPercent = (!isNoBeforeWeightStage && expectedWeight > 0) ? (netLossGainWeight / expectedWeight) * 100 : 0;
  const weightDeltaLabel = netLossGainWeight > 0 ? 'Loss' : netLossGainWeight < 0 ? 'Gain' : 'Balanced';
  const weightFormulaText = isNoBeforeWeightStage
    ? 'No metal-loss formula for this stage because before-weight tracking is skipped.'
    : `${numericInputWeight.toFixed(3)}g input + ${materialWeightUsed.toFixed(3)}g material used - ${numericAfterWeight.toFixed(3)}g output = ${netLossGainWeight.toFixed(3)}g net loss/gain`;
  const treeDistributionTotalPieces = treePieceDistribution.reduce((sum, row) => sum + (Number(row.pieces) || 0), 0);

  const handleTreeDistributionChange = (stageRowId, value) => {
    setTreePieceDistribution((prev) =>
      prev.map((row) =>
        row.stage_id === stageRowId
          ? { ...row, pieces: Math.max(0, parseInt(value || 0, 10) || 0) }
          : row
      )
    );
  };

  const renderStageSpecificFields = () => {
    const sCode = normalizeStageCode(stage.stage_code || stage.stage_name);

    if (sCode === 'WAX_CREATION') {
      const rejected = Math.max((parseInt(stageInputs.molds_injected, 10) || 0) - (parseInt(stageInputs.good_patterns, 10) || 0), 0);
      const yieldPct = (parseInt(stageInputs.molds_injected, 10) || 0) > 0
        ? ((parseInt(stageInputs.good_patterns, 10) || 0) / parseInt(stageInputs.molds_injected, 10) * 100).toFixed(1)
        : 0;

      return (
        <div className="p-4 bg-orange-50 border border-orange-100 rounded-lg space-y-4">
          <h4 className="text-xs font-bold text-orange-800 uppercase flex items-center gap-1"><AlertTriangle size={12} /> Wax Pattern Metrics</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Molds Injected <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black" placeholder="0"
                value={stageInputs.molds_injected || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, molds_injected: e.target.value })}
                disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Good Patterns <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black" placeholder="0"
                value={stageInputs.good_patterns || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, good_patterns: e.target.value })}
                disabled={isCompleted} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Rejected Patterns</label>
              <input type="number" className="w-full p-2 border rounded bg-red-50 text-sm text-red-700 font-semibold"
                value={rejected} disabled />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Pattern Yield %</label>
              <input type="text" className={`w-full p-2 border rounded text-sm font-semibold ${parseFloat(yieldPct) >= 90 ? 'bg-green-50 text-green-700' : parseFloat(yieldPct) >= 70 ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}
                value={`${yieldPct}%`} disabled />
            </div>
          </div>
          {rejected > 0 && (
            <div>
              <label className="text-xs font-bold text-slate-700">Rejection Reason <span className="text-red-500">*</span></label>
              <select className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.rejection_reason || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, rejection_reason: e.target.value })}
                disabled={isCompleted}>
                <option value="">-- Select Reason --</option>
                <option value="Air Bubbles">Air Bubbles</option>
                <option value="Shrinkage">Shrinkage</option>
                <option value="Incomplete Fill">Incomplete Fill</option>
                <option value="Cracked">Cracked</option>
                <option value="Warped">Warped</option>
                <option value="Surface Blemish">Surface Blemish</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold text-slate-700">Wax Batch Number</label>
            <input type="text" className="w-full p-2 border rounded bg-white text-sm text-black" placeholder="e.g. WB-2026-031"
              value={stageInputs.wax_batch_number || ''}
              onChange={(e) => setStageInputs({ ...stageInputs, wax_batch_number: e.target.value })}
              disabled={isCompleted} />
          </div>
        </div>
      );
    }

    if (sCode === 'WAX_CLEANING') {
      const received = parseInt(stageInputs.patterns_received, 10) || parseInt(prefillData?.pieces_in, 10) || incomingPieces || 0;
      const cleaned = parseInt(stageInputs.patterns_cleaned, 10) || 0;
      const rework = parseInt(stageInputs.patterns_rework, 10) || 0;
      const scrapped = Math.max(received - cleaned - rework, 0);
      const scrapPct = received > 0 ? (scrapped / received * 100).toFixed(1) : 0;

      return (
        <div className="p-4 bg-cyan-50 border border-cyan-100 rounded-lg space-y-4">
          <h4 className="text-xs font-bold text-cyan-800 uppercase flex items-center gap-1"><Beaker size={12} /> Wax Cleaning Metrics</h4>
          <div>
            <label className="text-xs font-bold text-slate-700">Patterns Received (from Wax Creation)</label>
            <input type="number" className="w-full p-2 border rounded bg-slate-100 text-sm text-black font-semibold" value={received} disabled />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Patterns Cleaned <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black"
                placeholder="0"
                value={stageInputs.patterns_cleaned || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, patterns_cleaned: e.target.value })}
                disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Patterns Need Rework</label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black"
                placeholder="0"
                value={stageInputs.patterns_rework || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, patterns_rework: e.target.value })}
                disabled={isCompleted} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Patterns Scrapped</label>
              <input type="number" className="w-full p-2 border rounded bg-red-50 text-sm text-red-700 font-semibold" value={scrapped} disabled />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Scrap Rate</label>
              <input type="text" className={`w-full p-2 border rounded text-sm font-semibold ${parseFloat(scrapPct) <= 5 ? 'bg-green-50 text-green-700' : parseFloat(scrapPct) <= 15 ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}
                value={`${scrapPct}%`} disabled />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700">Cleaning Method <span className="text-red-500">*</span></label>
            <select className="w-full p-2 border rounded bg-white text-sm text-black"
              value={stageInputs.cleaning_method || ''}
              onChange={(e) => setStageInputs({ ...stageInputs, cleaning_method: e.target.value })}
              disabled={isCompleted}>
              <option value="">-- Select Method --</option>
              <option value="Manual Scraping">Manual Scraping</option>
              <option value="Chemical Bath">Chemical Bath</option>
              <option value="Heat Gun">Heat Gun</option>
              <option value="Ultrasonic">Ultrasonic</option>
            </select>
          </div>
        </div>
      );
    }

    if (sCode === 'WAX_SETTING') {
      const issued = parseInt(stageInputs.stones_issued, 10) || 0;
      const set = parseInt(stageInputs.stones_set, 10) || 0;
      const broken = parseInt(stageInputs.stones_broken, 10) || 0;
      const returned = Math.max(issued - set - broken, 0);
      const balanceOk = issued === 0 || (issued === set + broken + returned);

      return (
        <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-lg space-y-4">
          <h4 className="text-xs font-bold text-indigo-800 uppercase flex items-center gap-1"><Gem size={12} /> Stone Setting Metrics</h4>
          <div>
            <label className="text-xs font-bold text-slate-700">Patterns Received</label>
            <input type="number" className="w-full p-2 border rounded bg-slate-100 text-sm text-black"
              value={parseInt(prefillData?.pieces_in, 10) || incomingPieces || 0} disabled />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-700">Stones Issued <span className="text-red-500">*</span></label>
              <input type="number" className={`w-full p-2 border rounded text-sm ${totalIssuedStonePieces > 0 ? 'bg-slate-100 text-slate-700 font-semibold' : 'bg-white text-black'}`} placeholder="0"
                value={stageInputs.stones_issued || ''} onChange={(e) => setStageInputs({ ...stageInputs, stones_issued: e.target.value })} disabled={isCompleted || totalIssuedStonePieces > 0} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Stones Set <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black" placeholder="0"
                value={stageInputs.stones_set || ''} onChange={(e) => setStageInputs({ ...stageInputs, stones_set: e.target.value })} disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-red-600">Stones Broken <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border border-red-200 rounded bg-white text-sm text-red-600" placeholder="0"
                value={stageInputs.stones_broken || ''} onChange={(e) => setStageInputs({ ...stageInputs, stones_broken: e.target.value })} disabled={isCompleted} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Stones Returned (unused)</label>
              <input type="number" className="w-full p-2 border rounded bg-blue-50 text-sm text-blue-700 font-semibold" value={returned} disabled />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Balance Check</label>
              <div className={`w-full p-2 border rounded text-sm font-bold text-center ${balanceOk ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-300'}`}>
                {balanceOk ? 'BALANCED' : 'MISMATCH - STONES MISSING!'}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Patterns With Stones <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black" placeholder="0"
                value={stageInputs.patterns_with_stones || ''} onChange={(e) => setStageInputs({ ...stageInputs, patterns_with_stones: e.target.value })} disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Setting Quality <span className="text-red-500">*</span></label>
              <select className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.setting_quality || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, setting_quality: e.target.value })}
                disabled={isCompleted}>
                <option value="">-- Select --</option>
                <option value="Tight">Tight (Secure)</option>
                <option value="Acceptable">Acceptable</option>
                <option value="Loose">Loose (Needs Redo)</option>
              </select>
            </div>
          </div>
        </div>
      );
    }

    if (sCode === 'TREE_MAKING') {
      const treeWeight = parseFloat(completeData.after_weight) || 0;
      const expectedMetal = (treeWeight * 10.5).toFixed(1);

      return (
        <div className="p-4 bg-green-50 border border-green-100 rounded-lg space-y-4">
          <h4 className="text-xs font-bold text-green-800 uppercase flex items-center gap-1"><GitMerge size={12} /> Tree Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Patterns Attached <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.patterns_attached || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, patterns_attached: e.target.value })}
                disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Sprue/Trunk Weight (g) <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.sprue_weight || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, sprue_weight: e.target.value })}
                disabled={isCompleted} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Flask ID <span className="text-red-500">*</span></label>
              <input type="text" className="w-full p-2 border rounded bg-white text-sm text-black"
                placeholder="F-101"
                value={stageInputs.flask_id || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, flask_id: e.target.value })}
                disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Expected Metal Required (g)</label>
              <input type="text" className="w-full p-2 border rounded bg-blue-50 text-sm text-blue-700 font-semibold"
                value={treeWeight > 0 ? `${expectedMetal}g (auto)` : '—'} disabled />
            </div>
          </div>
          {stage?.tree_batch_id && (
            <div className="rounded-lg border border-green-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-bold text-slate-700">Job Cards Used In This Tree</p>
                <div className="flex items-center gap-2">
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                    Jobs: {treeBatchStages.length}
                  </span>
                  <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">
                    Batch: {stage.tree_batch_id}
                  </span>
                </div>
              </div>
              {loadingTreeBatch ? (
                <div className="text-xs text-slate-500">Loading batch job cards...</div>
              ) : treePieceDistribution.length === 0 ? (
                <div className="text-xs text-slate-500">No linked job cards found for this tree batch.</div>
              ) : (
                <div className="space-y-2">
                  {treePieceDistribution.map((row) => (
                    <div key={row.stage_id} className="grid grid-cols-12 gap-2 items-center rounded border border-slate-200 p-2">
                      <div className="col-span-7 text-xs font-semibold text-slate-700">
                        {row.job_card_no || `Job Card ${row.job_card_id}`}
                      </div>
                      <div className="col-span-5">
                        <input
                          type="number"
                          min="0"
                          className="w-full p-1.5 border rounded bg-white text-sm"
                          value={row.pieces}
                          onChange={(e) => handleTreeDistributionChange(row.stage_id, e.target.value)}
                          disabled={isCompleted}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="pt-1 text-xs font-bold text-indigo-700">
                    Total Pieces (all job cards): {treeDistributionTotalPieces}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    if (sCode === 'MOLDING' || sCode === 'CASTING') {
      const charged = parseFloat(stageInputs.metal_charged) || 0;
      const grossOut = parseFloat(completeData.after_weight) || 0;
      const loss = charged > 0 ? (charged - grossOut).toFixed(3) : '0';
      const lossPct = charged > 0 ? ((charged - grossOut) / charged * 100).toFixed(2) : '0';

      return (
        <div className="p-4 bg-red-50 border border-red-100 rounded-lg space-y-4">
          <h4 className="text-xs font-bold text-red-800 uppercase flex items-center gap-1"><Flame size={12} /> Casting Parameters</h4>
          <div>
            <label className="text-xs font-bold text-slate-700">Metal Charged (g) <span className="text-red-500">*</span></label>
            <input type="number" step="0.001" className="w-full p-2 border rounded bg-white text-sm text-black"
              value={stageInputs.metal_charged || ''}
              onChange={(e) => setStageInputs({ ...stageInputs, metal_charged: e.target.value })}
              disabled={isCompleted} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Flask Temp (C) <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.flask_temperature || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, flask_temperature: e.target.value })}
                disabled={isCompleted} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Pouring Temp (C) <span className="text-red-500">*</span></label>
              <input type="number" className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.pouring_temperature || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, pouring_temperature: e.target.value })}
                disabled={isCompleted} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Metal Purity <span className="text-red-500">*</span></label>
              <select className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.metal_purity || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, metal_purity: e.target.value })}
                disabled={isCompleted}>
                <option value="">-- Select --</option>
                <option value="999 Fine Silver">999 Fine Silver</option>
                <option value="925 Sterling">925 Sterling</option>
                <option value="22K Gold">22K Gold</option>
                <option value="18K Gold">18K Gold</option>
                <option value="14K Gold">14K Gold</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Casting Method <span className="text-red-500">*</span></label>
              <select className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.casting_method || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, casting_method: e.target.value })}
                disabled={isCompleted}>
                <option value="">-- Select --</option>
                <option value="Centrifugal">Centrifugal</option>
                <option value="Vacuum">Vacuum</option>
                <option value="Gravity">Gravity</option>
                <option value="Pressure">Pressure</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Metal Loss (g)</label>
              <input type="text" className={`w-full p-2 border rounded text-sm font-semibold ${parseFloat(lossPct) <= 4 ? 'bg-green-50 text-green-700' : parseFloat(lossPct) <= 6 ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}
                value={charged > 0 ? `${loss}g (${lossPct}%)` : '—'} disabled />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Casting Defects <span className="text-red-500">*</span></label>
              <select className="w-full p-2 border rounded bg-white text-sm text-black"
                value={stageInputs.casting_defects || ''}
                onChange={(e) => setStageInputs({ ...stageInputs, casting_defects: e.target.value })}
                disabled={isCompleted}>
                <option value="None">None</option>
                <option value="Porosity">Porosity</option>
                <option value="Shrinkage">Shrinkage</option>
                <option value="Misrun">Misrun</option>
                <option value="Cold Shut">Cold Shut</option>
                <option value="Gas Holes">Gas Holes</option>
              </select>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const nextStageTarget = getNextPreStageTarget(stage.stage_code, stageFlow);

  const handleComplete = async ({ skipQc = false, nextScope = null } = {}) => {
    if (!completeData.after_weight) return alert("Enter After Weight");
    if (isTreeMakingStage && stage?.tree_batch_id && treePieceDistribution.length > 0 && treeDistributionTotalPieces <= 0) {
      return alert("Enter valid pieces for at least one job card in this tree batch.");
    }

    const normalizedStageCode = normalizeStageCode(stage?.stage_code || stage?.stage_name);
    if (normalizedStageCode === 'WAX_CREATION') {
      const moldsInjected = parseInt(stageInputs.molds_injected, 10);
      const goodPatterns = parseInt(stageInputs.good_patterns, 10);
      const rejectedPatterns = Math.max((Number.isNaN(moldsInjected) ? 0 : moldsInjected) - (Number.isNaN(goodPatterns) ? 0 : goodPatterns), 0);

      if (Number.isNaN(moldsInjected)) return alert('Enter Molds Injected');
      if (Number.isNaN(goodPatterns)) return alert('Enter Good Patterns');
      if (goodPatterns > moldsInjected) return alert('Good Patterns cannot be greater than Molds Injected');
      if (rejectedPatterns > 0 && !stageInputs.rejection_reason) {
        return alert('Select Rejection Reason when rejected patterns are present');
      }
    }
    if (normalizedStageCode === 'WAX_CLEANING') {
      const received = parseInt(stageInputs.patterns_received, 10)
        || parseInt(prefillData?.pieces_in, 10)
        || incomingPieces
        || 0;
      const cleaned = parseInt(stageInputs.patterns_cleaned, 10);
      const rework = parseInt(stageInputs.patterns_rework, 10) || 0;

      if (Number.isNaN(cleaned)) return alert('Enter Patterns Cleaned');
      if (!stageInputs.cleaning_method) return alert('Select Cleaning Method');
      if (cleaned + rework > received) {
        return alert('Patterns Cleaned and Rework cannot exceed Patterns Received');
      }
    }
    if (normalizedStageCode === 'WAX_SETTING') {
      const stonesIssued = parseInt(stageInputs.stones_issued, 10);
      const stonesSet = parseInt(stageInputs.stones_set, 10);
      const stonesBroken = parseInt(stageInputs.stones_broken, 10);
      const patternsWithStones = parseInt(stageInputs.patterns_with_stones, 10);
      const patternsReceived = parseInt(prefillData?.pieces_in, 10) || incomingPieces || 0;

      if (Number.isNaN(stonesIssued)) return alert('Enter Stones Issued');
      if (Number.isNaN(stonesSet)) return alert('Enter Stones Set');
      if (Number.isNaN(stonesBroken)) return alert('Enter Stones Broken');
      if (Number.isNaN(patternsWithStones)) return alert('Enter Patterns With Stones');
      if (!stageInputs.setting_quality) return alert('Select Setting Quality');
      if (stonesSet + stonesBroken > stonesIssued) {
        return alert('Stones Set and Broken cannot exceed Stones Issued');
      }
      if (patternsWithStones > patternsReceived) {
        return alert('Patterns With Stones cannot exceed Patterns Received');
      }
    }
    if (normalizedStageCode === 'TREE_MAKING') {
      const patternsAttached = parseInt(stageInputs.patterns_attached, 10);
      const sprueWeight = parseFloat(stageInputs.sprue_weight);

      if (Number.isNaN(patternsAttached)) return alert('Enter Patterns Attached');
      if (Number.isNaN(sprueWeight)) return alert('Enter Sprue/Trunk Weight');
      if (!stageInputs.flask_id) return alert('Enter Flask ID');
    }
    if (normalizedStageCode === 'MOLDING' || normalizedStageCode === 'CASTING') {
      const metalCharged = parseFloat(stageInputs.metal_charged);
      const flaskTemperature = parseFloat(stageInputs.flask_temperature);
      const pouringTemperature = parseFloat(stageInputs.pouring_temperature);

      if (Number.isNaN(metalCharged)) return alert('Enter Metal Charged');
      if (Number.isNaN(flaskTemperature)) return alert('Enter Flask Temperature');
      if (Number.isNaN(pouringTemperature)) return alert('Enter Pouring Temperature');
      if (!stageInputs.metal_purity) return alert('Select Metal Purity');
      if (!stageInputs.casting_method) return alert('Select Casting Method');
      if (!stageInputs.casting_defects) return alert('Select Casting Defects');
    }

    if (stage.worker_count > 1) {
      for (const w of stageWorkers) {
        if (!contributions[w.worker_id]?.hours) {
          return alert(`Please enter hours contributed for ${w.name}`);
        }
      }
    }

    setLoading(true);
    try {
      if (!stageId) throw new Error('Stage ID is missing.');
      const stageSpecificAutoPayload = normalizedStageCode === 'WAX_CLEANING'
        ? {
            patterns_received: parseInt(stageInputs.patterns_received, 10) || parseInt(prefillData?.pieces_in, 10) || incomingPieces || 0,
          }
        : {};
      const payload = {
        after_weight: parseFloat(completeData.after_weight),
        pieces_count: parseInt(completeData.pieces_count) || 0,
        remarks: completeData.remarks,
        skip_qc: skipQc,
        material_consumption: issuedMaterials.map((row) => {
          const values = getMaterialDraftValues(row);
          return {
            material_id: row.material_id,
            use_full_qty: values.useFullQty,
            qty_used: values.qtyUsed,
            qty_returned: values.qtyLeft
          };
        }),
        ...(isTreeMakingStage && stage?.tree_batch_id
          ? {
              batch_stage_ids: treePieceDistribution.map((row) => row.stage_id).filter(Boolean),
              tree_piece_distribution: treePieceDistribution.map((row) => ({
                stage_id: row.stage_id,
                job_card_id: row.job_card_id,
                pieces: Number(row.pieces) || 0,
              })),
            }
          : {}),
        ...stageSpecificAutoPayload,
        ...stageInputs
      };
      const completeRes = await api.completeStage(stageId, payload);
      const resultData = completeRes?.data || completeRes;

      if (stage.worker_count > 1) {
        for (const w of stageWorkers) {
          const cont = contributions[w.worker_id];
          await productionApi.put(`/job-cards/stages/${stageId}/workers/${w.worker_id}/complete-contribution`, {
            hours_contributed: parseFloat(cont.hours) || 0,
            pieces_contributed: parseInt(cont.pieces) || 0
          });
        }
      }

      // Upload photos
      if (beforePhoto) {
        const b64 = await fileToBase64(beforePhoto);
        await api.uploadStagePhoto(stageId, b64, 'before', 'Before processing');
      }
      if (afterPhoto) {
        const b64 = await fileToBase64(afterPhoto);
        await api.uploadStagePhoto(stageId, b64, 'after', 'After processing');
      }

      // Show completion summary if material data and no immediate navigation
      const stageTransition = {
        currentStageId: stageId,
        nextStageCode: nextStageTarget?.stageCode || null,
        skipQc,
      };

      if (resultData?.data?.material_summary && !nextScope) {
        setCompletionResult(resultData.data);
        await onSave(stageTransition);
        return;
      }

      await onSave(stageTransition);

      if (nextScope && nextStageTarget) {
        const params = new URLSearchParams({ stage_code: nextStageTarget.stageCode });
        if (nextScope === 'order' && jobCard.order_id) {
          params.set('order_id', jobCard.order_id);
        } else {
          params.set('job_card_no', jobCard.job_card_no || String(jobCard.job_card_id || ''));
          if (jobCard.order_id) params.set('order_id', jobCard.order_id);
        }
        navigate(`${nextStageTarget.route}?${params.toString()}`);
      }
    } catch (e) {
      alert("Failed to complete stage. " + (e.response?.data?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] overflow-hidden bg-white text-slate-900">
      <div className="bg-white border-b border-slate-200 px-8 py-5 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-slate-400 hover:text-indigo-600"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              {stage.stage_name}
              <span className="text-sm font-normal text-slate-600 bg-white px-2 py-1 rounded-full border border-slate-200">
                {isCompleted ? 'Completed' : 'Execution'}
              </span>
            </h1>
            <div className="flex gap-4 text-xs text-slate-400 mt-1">
              <span>JC: <strong>{jobCard.job_card_no}</strong></span>
              {/* âœ… FIX: Real order_id from API in execution screen header */}
              <span>
                Order: <strong className="text-blue-600">
                  {jobCard.order_id ?? 'N/A'}
                </strong>
              </span>
              <span className="text-blue-600 font-medium">Worker: {stage.worker_name || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto space-y-6">

          <div className="bg-white p-6 rounded-xl border border-slate-200 flex justify-between items-center">
            <div>
              <h4 className="text-slate-900 font-bold text-lg mb-1">Process Started</h4>
              {!isNoBeforeWeightStage && (
                <p className="text-blue-600 text-sm">
                  Input Weight: <strong>{stage.before_weight} g</strong>
                </p>
              )}
            </div>
            <div className="text-right">
              <span className="text-xs text-slate-400 uppercase font-bold">Start Time</span>
              <div className="text-slate-900 font-mono">
                {stage.started_at ? new Date(stage.started_at).toLocaleString() : '-'}
              </div>
            </div>
          </div>

          <CountdownAlert stage={stage} />

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm animate-in slide-in-from-bottom-2">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="text-[#3B82F6]" />
              Output & Completion
            </h3>

            <div className="mb-6">
              {renderStageSpecificFields()}
            </div>

            <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800">
                <Package size={15} /> Current Stage Used Materials
              </h4>
              {issuedMaterials.length === 0 ? (
                <div className="text-xs text-slate-500">No materials recorded for this stage.</div>
              ) : (
                <div className="overflow-x-auto mobile-scroll-table">
                  <table className="w-full min-w-[720px] border-collapse">
                    <thead>
                      <tr className="bg-white text-[11px] uppercase text-slate-500">
                        <th className="border border-slate-200 px-3 py-2 text-left">Material</th>
                        <th className="border border-slate-200 px-3 py-2 text-left">Code</th>
                        <th className="border border-slate-200 px-3 py-2 text-right">Issued</th>
                        <th className="border border-slate-200 px-3 py-2 text-right">Used</th>
                        <th className="border border-slate-200 px-3 py-2 text-right">Returned</th>
                        <th className="border border-slate-200 px-3 py-2 text-right">Wastage</th>
                        <th className="border border-slate-200 px-3 py-2 text-right">Effective Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issuedMaterials.map((mat, idx) => {
                        const issued = parseFloat(mat.qty_issued || 0);
                        const liveValues = !isCompleted ? getMaterialDraftValues(mat) : null;
                        const used = liveValues ? liveValues.qtyUsed : parseFloat(mat.qty_used || 0);
                        const returned = liveValues ? liveValues.qtyLeft : parseFloat(mat.qty_returned || 0);
                        const wastage = parseFloat(mat.qty_wastage || 0);
                        const effectiveUsed = liveValues ? liveValues.qtyUsed : getEffectiveUsedQty(mat);
                        return (
                          <tr key={`${mat.id || mat.material_id}-${idx}`} className="bg-white text-sm">
                            <td className="border border-slate-200 px-3 py-2 text-slate-800">{mat.material_name || `Material #${mat.material_id}`}</td>
                            <td className="border border-slate-200 px-3 py-2 text-slate-600">{mat.material_code || mat.material_id}</td>
                            <td className="border border-slate-200 px-3 py-2 text-right">{issued.toFixed(3)} {mat.unit}</td>
                            <td className="border border-slate-200 px-3 py-2 text-right">{used.toFixed(3)} {mat.unit}</td>
                            <td className="border border-slate-200 px-3 py-2 text-right">{returned.toFixed(3)} {mat.unit}</td>
                            <td className="border border-slate-200 px-3 py-2 text-right">{wastage.toFixed(3)} {mat.unit}</td>
                            <td className="border border-slate-200 px-3 py-2 text-right font-semibold text-indigo-700">{effectiveUsed.toFixed(3)} {mat.unit}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {issuedStones.length > 0 && (
              <div className="mb-6 rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-fuchsia-800">
                  <Gem size={15} /> Current Stage Assigned Stones
                </h4>
                <div className="overflow-x-auto mobile-scroll-table">
                  <table className="w-full min-w-[920px] border-collapse">
                    <thead>
                      <tr className="bg-white text-[11px] uppercase text-slate-500">
                        <th className="border border-fuchsia-100 px-3 py-2 text-left">Stone</th>
                        <th className="border border-fuchsia-100 px-3 py-2 text-left">Type</th>
                        <th className="border border-fuchsia-100 px-3 py-2 text-left">Size</th>
                        <th className="border border-fuchsia-100 px-3 py-2 text-left">Quality</th>
                        <th className="border border-fuchsia-100 px-3 py-2 text-left">Clarity</th>
                        <th className="border border-fuchsia-100 px-3 py-2 text-left">Color</th>
                        <th className="border border-fuchsia-100 px-3 py-2 text-right">Issued</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issuedStones.map((stone, idx) => (
                        <tr key={`${stone.material_id || stone.stone_name || 'stone'}-${idx}`} className="bg-white text-sm">
                          <td className="border border-fuchsia-100 px-3 py-2 text-slate-800 font-medium">{stone.stone_name || stone.name || 'Stone'}</td>
                          <td className="border border-fuchsia-100 px-3 py-2 text-slate-600">{stone.stone_type || stone.type || '-'}</td>
                          <td className="border border-fuchsia-100 px-3 py-2 text-slate-600">{stone.size || '-'}</td>
                          <td className="border border-fuchsia-100 px-3 py-2 text-slate-600">{stone.quality_grade || '-'}</td>
                          <td className="border border-fuchsia-100 px-3 py-2 text-slate-600">{stone.clarity || '-'}</td>
                          <td className="border border-fuchsia-100 px-3 py-2 text-slate-600">{stone.color || '-'}</td>
                          <td className="border border-fuchsia-100 px-3 py-2 text-right font-semibold text-fuchsia-700">
                            {Number(stone.count || stone.qty_issued || 0)} {stone.unit || 'PCS'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isCompleted && issuedMaterials.length > 0 && (
              <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-indigo-800">
                  <Package size={15} /> Material Usage For Completion
                </h4>
                <div className="overflow-x-auto mobile-scroll-table">
                  <table className="w-full min-w-[860px] border-collapse">
                    <thead>
                      <tr className="bg-white text-[11px] uppercase text-slate-500">
                        <th className="border border-indigo-100 px-3 py-2 text-left">Material</th>
                        <th className="border border-indigo-100 px-3 py-2 text-right">Issued</th>
                        <th className="border border-indigo-100 px-3 py-2 text-center">100% Used</th>
                        <th className="border border-indigo-100 px-3 py-2 text-right">Used Now</th>
                        <th className="border border-indigo-100 px-3 py-2 text-right">Left / Return</th>
                        <th className="border border-indigo-100 px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issuedMaterials.map((mat, idx) => {
                        const values = getMaterialDraftValues(mat);
                        return (
                          <tr key={`draft-${mat.id || mat.material_id}-${idx}`} className="bg-white text-sm">
                            <td className="border border-indigo-100 px-3 py-2">
                              <div className="font-medium text-slate-800">{mat.material_name || `Material #${mat.material_id}`}</div>
                              <div className="text-[11px] text-slate-500">{mat.material_code || mat.material_id}</div>
                            </td>
                            <td className="border border-indigo-100 px-3 py-2 text-right font-mono">{values.issued.toFixed(3)} {mat.unit}</td>
                            <td className="border border-indigo-100 px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={values.useFullQty}
                                onChange={(e) => updateMaterialConsumptionDraft(mat.material_id, { use_full_qty: e.target.checked })}
                                className="h-4 w-4 accent-indigo-600"
                              />
                            </td>
                            <td className="border border-indigo-100 px-3 py-2 text-right">
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                disabled={values.useFullQty}
                                value={values.useFullQty ? values.qtyUsed.toFixed(3) : (materialConsumptionDraft[mat.material_id]?.qty_used ?? values.qtyUsed.toFixed(3))}
                                onChange={(e) => updateMaterialConsumptionDraft(mat.material_id, { qty_used: e.target.value, use_full_qty: false })}
                                className="w-28 rounded border border-slate-300 px-2 py-1 text-right font-mono outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100"
                              />
                            </td>
                            <td className="border border-indigo-100 px-3 py-2 text-right font-mono text-emerald-700">{values.qtyLeft.toFixed(3)} {mat.unit}</td>
                            <td className="border border-indigo-100 px-3 py-2">
                              {values.qtyLeft > 0 ? (
                                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700 border border-emerald-200">
                                  Auto return on submit
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-1 text-[10px] font-bold text-indigo-700 border border-indigo-200">
                                  Fully consumed
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  {['MOLDING', 'CASTING'].includes(stage.stage_code) ? 'Gross Weight (Metal + Tree)' : isNoBeforeWeightStage ? 'Wax Output Weight (g)' : 'After Weight (g)'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number" step="0.01"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00"
                  value={completeData.after_weight}
                  onChange={(e) => setCompleteData({ ...completeData, after_weight: e.target.value })}
                  disabled={isCompleted}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Total Pieces Produced</label>
                <input
                  type="number"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0"
                  value={completeData.pieces_count}
                  onChange={(e) => setCompleteData({ ...completeData, pieces_count: e.target.value })}
                  disabled={isCompleted}
                />
              </div>
            </div>

            {/* Live Auto-Calculations Panel */}
            {(completeData.after_weight || expectedWeight > 0) && (
              <div className="bg-gradient-to-r from-slate-50 to-indigo-50 p-4 rounded-xl border border-indigo-100 mb-4">
                <h4 className="text-xs font-bold text-indigo-800 uppercase mb-3 flex items-center gap-2">
                  <Scale size={14} /> Weight Calculations
                </h4>
                <div className="mb-3 rounded-lg border border-indigo-100 bg-white px-3 py-2 text-[11px] text-slate-600">
                  <span className="font-bold text-indigo-700">Formula:</span> {weightFormulaText}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500 font-bold uppercase">Input Weight</div>
                    <div className="text-xl font-bold font-mono text-slate-800">{numericInputWeight.toFixed(3)}g</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500 font-bold uppercase">Material Weight</div>
                    <div className="text-xl font-bold font-mono text-indigo-700">{materialWeightUsed.toFixed(3)}g</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500 font-bold uppercase">Expected Weight</div>
                    <div className="text-xl font-bold font-mono text-slate-900">{expectedWeight.toFixed(3)}g</div>
                    <div className="text-[10px] text-slate-500 mt-1">Input + Materials</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500 font-bold uppercase">Net Weight</div>
                    <div className="text-xl font-bold font-mono text-slate-800">{numericAfterWeight.toFixed(3)}g</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500 font-bold uppercase">Wastage</div>
                    <div className={`text-xl font-bold font-mono ${wastageWeight > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                      {wastageWeight.toFixed(3)}g
                    </div>
                    <div className="text-[10px] mt-1 text-slate-500">Only positive loss is counted as wastage</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500 font-bold uppercase">Metal Loss</div>
                    <div className={`text-xl font-bold font-mono ${metalLossPercent > 5 ? 'text-red-600' : metalLossPercent > 2 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {metalLossPercent.toFixed(2)}%
                    </div>
                    <div className="text-[10px] mt-1 text-slate-500">{weightDeltaLabel}</div>
                  </div>
                </div>
              </div>
            )}

            {(completeData.after_weight || completeData.pieces_count || completeData.remarks) && (
              <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-800">
                  <Info size={14} /> Submission Summary
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <div className="text-[11px] uppercase text-blue-500">Input Weight</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{stage.before_weight || 0} g</div>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <div className="text-[11px] uppercase text-blue-500">Material Weight</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{materialWeightUsed.toFixed(3)} g</div>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <div className="text-[11px] uppercase text-blue-500">Expected Weight</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{expectedWeight.toFixed(3)} g</div>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <div className="text-[11px] uppercase text-blue-500">Net Loss / Gain</div>
                    <div className={`mt-1 text-sm font-semibold ${netLossGainWeight > 0 ? 'text-orange-600' : netLossGainWeight < 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                      {netLossGainWeight.toFixed(3)} g
                    </div>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <div className="text-[11px] uppercase text-blue-500">After Weight</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{completeData.after_weight || '-'} g</div>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <div className="text-[11px] uppercase text-blue-500">Pieces Produced</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{completeData.pieces_count || 0}</div>
                  </div>
                </div>
                <div className="mt-3 rounded-lg border border-blue-100 bg-white p-3 text-[11px] text-slate-600">
                  <span className="font-bold text-blue-700">Auto calculation:</span> {weightFormulaText}
                </div>
              </div>
            )}

            <div className="space-y-2 mb-6">
              <label className="text-sm font-semibold text-slate-700">Remarks</label>
              <textarea
                className="w-full p-2.5 border border-slate-300 rounded-lg outline-none h-20 resize-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Notes on wastage or defects..."
                value={completeData.remarks}
                onChange={(e) => setCompleteData({ ...completeData, remarks: e.target.value })}
                disabled={isCompleted}
              />
            </div>

            {stage.worker_count > 1 && stageWorkers.length > 0 && (
              <div className="mb-6 p-4 border rounded-lg bg-slate-50 border-indigo-100">
                <h4 className="text-sm font-bold text-indigo-900 mb-3 flex items-center gap-2">
                  <User size={16} className="text-indigo-600" /> Record Worker Contributions
                </h4>
                <div className="space-y-3">
                  {stageWorkers.map(w => (
                    <div key={w.worker_id} className="flex gap-4 items-center bg-white p-3 border border-slate-200 rounded-lg shadow-sm">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-700">{w.name}</div>
                        <div className="text-xs text-slate-400 capitalize">{w.role_in_stage}</div>
                      </div>
                      <div className="w-24">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Hours <span className="text-red-500">*</span></label>
                        <input type="number" step="0.5" placeholder="0" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                          value={contributions[w.worker_id]?.hours || ''}
                          onChange={e => setContributions({ ...contributions, [w.worker_id]: { ...contributions[w.worker_id], hours: e.target.value } })}
                          disabled={isCompleted} />
                      </div>
                      <div className="w-24">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Pieces</label>
                        <input type="number" placeholder="0" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                          value={contributions[w.worker_id]?.pieces || ''}
                          onChange={e => setContributions({ ...contributions, [w.worker_id]: { ...contributions[w.worker_id], pieces: e.target.value } })}
                          disabled={isCompleted} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photos Section */}
            {!isCompleted && (
              <div className="mb-6 p-4 border rounded-lg bg-slate-50 border-slate-200">
                <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Camera size={16} className="text-slate-600" /> Attached Photos / Evidence
                </h4>
                <div className="grid grid-cols-2 gap-6">
                  {/* Before Photo */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase">Before Photo</label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors relative cursor-pointer group h-32 flex flex-col items-center justify-center">
                      <input type="file" accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={(e) => handlePhotoChange(e, 'before')} />
                      {beforePreview ? (
                        <img src={beforePreview} alt="Before Preview" className="absolute inset-0 w-full h-full object-cover rounded-xl" />
                      ) : (
                        <>
                          <UploadCloud size={24} className="mx-auto text-slate-400 group-hover:text-indigo-500 mb-2" />
                          <span className="text-xs font-medium text-slate-500">Click to upload or drag & drop</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* After Photo */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase">After Photo</label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors relative cursor-pointer group h-32 flex flex-col items-center justify-center">
                      <input type="file" accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={(e) => handlePhotoChange(e, 'after')} />
                      {afterPreview ? (
                        <img src={afterPreview} alt="After Preview" className="absolute inset-0 w-full h-full object-cover rounded-xl" />
                      ) : (
                        <>
                          <Camera size={24} className="mx-auto text-slate-400 group-hover:text-indigo-500 mb-2" />
                          <span className="text-xs font-medium text-slate-500">Click to upload or drag & drop</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {completionResult && (
              <div className="mb-6 rounded-xl border-2 border-emerald-200 bg-emerald-50/50 overflow-hidden">
                <div className="bg-emerald-100 px-6 py-3 border-b border-emerald-200">
                  <h4 className="font-bold text-emerald-800 text-sm flex items-center gap-2">
                    <CheckCircle2 size={16} /> Stage Completion Summary
                  </h4>
                </div>
                <div className="p-6">
                  <div className={`grid grid-cols-2 ${isNoBeforeWeightStage ? '' : 'md:grid-cols-4'} gap-4 mb-4`}>
                    {isNoBeforeWeightStage ? (
                      <>
                        <div className="text-center p-3 bg-white rounded-lg border border-emerald-100">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Output Weight</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{(completionResult.after_weight || 0).toFixed(3)}g</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg border border-emerald-100">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Pieces Produced</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{completionResult.pieces_produced || completionResult.net_pieces || 0}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-center p-3 bg-white rounded-lg border border-emerald-100">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Before Weight</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{(completionResult.before_weight || 0).toFixed(3)}g</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg border border-emerald-100">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">After Weight</div>
                          <div className="text-lg font-bold font-mono text-slate-800">{(completionResult.after_weight || 0).toFixed(3)}g</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg border border-emerald-100">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Metal Loss</div>
                          <div className="text-lg font-bold font-mono text-red-600">{(completionResult.wastage || 0).toFixed(3)}g</div>
                          <div className="text-[10px] mt-1 text-slate-500">Positive loss only</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg border border-emerald-100">
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Loss %</div>
                          <div className={`text-lg font-bold font-mono ${(completionResult.metal_loss_pct || 0) > 3 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {(completionResult.metal_loss_pct || 0).toFixed(2)}%
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {!isNoBeforeWeightStage && (
                    <div className="mb-4 rounded-lg border border-emerald-100 bg-white px-4 py-3 text-[11px] text-slate-600">
                      <span className="font-bold text-emerald-700">Formula:</span> Before Weight + Material Used - After Weight = Net Loss/Gain. Wastage shows only the positive part of that value.
                    </div>
                  )}

                  {completionResult.material_summary?.length > 0 && (
                    <div className="overflow-x-auto mb-4 mobile-scroll-table">
                      <table className="w-full min-w-[600px] border-collapse text-sm">
                        <thead>
                          <tr className="bg-white text-[10px] uppercase text-slate-500">
                            <th className="border border-emerald-100 px-3 py-2 text-left">Material</th>
                            <th className="border border-emerald-100 px-3 py-2 text-right">Issued</th>
                            <th className="border border-emerald-100 px-3 py-2 text-right">Used</th>
                            <th className="border border-emerald-100 px-3 py-2 text-right">Returned</th>
                            <th className="border border-emerald-100 px-3 py-2 text-right">Wastage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {completionResult.material_summary.map((mat, idx) => {
                            const issued = mat.qty_issued || 0;
                            const used = mat.qty_used || 0;
                            const usedPct = issued > 0 ? (used / issued) * 100 : 0;
                            const wastagePct = issued > 0 ? ((mat.qty_wastage || 0) / issued) * 100 : 0;
                            return (
                              <tr key={idx} className="bg-white">
                                <td className="border border-emerald-100 px-3 py-2 text-slate-800 font-medium">{mat.material_name}</td>
                                <td className="border border-emerald-100 px-3 py-2 text-right font-mono">{issued.toFixed(3)} {mat.unit}</td>
                                <td className="border border-emerald-100 px-3 py-2 text-right font-mono">
                                  {used.toFixed(3)} {mat.unit}
                                  <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                    <div className="h-full rounded-full flex">
                                      <div className="bg-blue-500 h-full" style={{ width: `${Math.min(usedPct, 100)}%` }} />
                                      <div className="bg-red-400 h-full" style={{ width: `${Math.min(wastagePct, 100 - usedPct)}%` }} />
                                    </div>
                                  </div>
                                </td>
                                <td className="border border-emerald-100 px-3 py-2 text-right font-mono text-slate-500">{(mat.qty_returned || 0).toFixed(3)}</td>
                                <td className="border border-emerald-100 px-3 py-2 text-right font-mono text-red-600 font-bold">{(mat.qty_wastage || 0).toFixed(3)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {completionResult.stone_summary?.length > 0 && (
                    <div className="overflow-x-auto mb-4 mobile-scroll-table">
                      <table className="w-full min-w-[920px] border-collapse text-sm">
                        <thead>
                          <tr className="bg-white text-[10px] uppercase text-slate-500">
                            <th className="border border-emerald-100 px-3 py-2 text-left">Stone</th>
                            <th className="border border-emerald-100 px-3 py-2 text-left">Type</th>
                            <th className="border border-emerald-100 px-3 py-2 text-left">Size</th>
                            <th className="border border-emerald-100 px-3 py-2 text-left">Quality</th>
                            <th className="border border-emerald-100 px-3 py-2 text-left">Clarity</th>
                            <th className="border border-emerald-100 px-3 py-2 text-right">Issued</th>
                            <th className="border border-emerald-100 px-3 py-2 text-right">Returned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {completionResult.stone_summary.map((stone, idx) => (
                            <tr key={`${stone.material_id || stone.stone_name || 'stone-summary'}-${idx}`} className="bg-white">
                              <td className="border border-emerald-100 px-3 py-2 text-slate-800 font-medium">{stone.stone_name || 'Stone'}</td>
                              <td className="border border-emerald-100 px-3 py-2 text-slate-600">{stone.stone_type || '-'}</td>
                              <td className="border border-emerald-100 px-3 py-2 text-slate-600">{stone.size || '-'}</td>
                              <td className="border border-emerald-100 px-3 py-2 text-slate-600">{stone.quality_grade || '-'}</td>
                              <td className="border border-emerald-100 px-3 py-2 text-slate-600">{stone.clarity || '-'}</td>
                              <td className="border border-emerald-100 px-3 py-2 text-right font-mono">{Number(stone.qty_issued || 0)} {stone.unit || 'PCS'}</td>
                              <td className="border border-emerald-100 px-3 py-2 text-right font-mono text-slate-500">{Number(stone.qty_returned || 0)} {stone.unit || 'PCS'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-emerald-800">
                      Any leftover material entered during completion was returned automatically.
                    </div>
                    <button
                      onClick={onBack}
                      className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-bold transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!isCompleted && !completionResult && (
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                {nextStageTarget && (
                  <>
                    <button
                      onClick={() => handleComplete({ skipQc: true, nextScope: 'job' })}
                      disabled={loading}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md flex items-center gap-2 transition-all disabled:bg-slate-300"
                    >
                      Skip QC + Next Job
                    </button>
                    <button
                      onClick={() => handleComplete({ skipQc: true, nextScope: 'order' })}
                      disabled={loading || !jobCard.order_id}
                      className="px-6 py-3 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-lg shadow-md flex items-center gap-2 transition-all disabled:bg-slate-300"
                    >
                      Skip QC + Same Order
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleComplete()}
                  disabled={loading}
                  className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-md flex items-center gap-2 transition-all disabled:bg-slate-300"
                >
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : <><CheckCircle2 size={20} /> Confirm & Submit to QC</>}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

// --- MAIN APP ---

export default function App() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState('WAX_CREATION');
  const [preStageNav, setPreStageNav] = useState([]);
  const [castingStageNav, setCastingStageNav] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [delayedStages, setDelayedStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('LIST');
  const [currentJob, setCurrentJob] = useState(null);
  const [currentStage, setCurrentStage] = useState(null);

  const workflowStageNav = [...preStageNav, ...castingStageNav];
  const moldingIndex = preStageNav.findIndex((stage) => stage.stage_code === 'MOLDING');
  const preStagesBeforeCasting = moldingIndex === -1 ? preStageNav : preStageNav.slice(0, moldingIndex + 1);
  const preStagesAfterCasting = moldingIndex === -1 ? [] : preStageNav.slice(moldingIndex + 1);
  const STAGE_SCREEN_MAP = Object.fromEntries(workflowStageNav.map((stage) => ([
    stage.stage_code,
    getPreStageInfo(stage.stage_code, stage.stage_name)
  ])));
  STAGE_SCREEN_MAP.DELAYED_STAGES = { title: 'Delayed Production', icon: AlertTriangle };

  const loadStageCatalog = useCallback(async () => {
    const catalog = await api.fetchStageCatalog();
    const preStageCandidates = [...(catalog.PRE_CASTING?.unique || [])];
    const preStages = dedupeStageNav(sortPreStageNav(preStageCandidates)
      .map((stage) => ({
      ...stage,
      ...getPreStageInfo(stage.stage_code, stage.stage_name)
    })));
    const castingStages = dedupeStageNav([...(catalog.CASTING?.unique || [])]
      .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
      .map((stage) => ({
      ...stage,
      ...getPreStageInfo(stage.stage_code, stage.stage_name)
    })));
    const resolvedPreStages = preStages.length ? preStages : DEFAULT_PRE_STAGE_NAV;
    const resolvedCastingStages = castingStages.length ? castingStages : DEFAULT_CASTING_STAGE_NAV;
    setPreStageNav(resolvedPreStages);
    setCastingStageNav(resolvedCastingStages);
    setActiveScreen((current) => {
      if (current === 'DELAYED_STAGES') return current;
      if ([...resolvedPreStages, ...resolvedCastingStages].some((stage) => stage.stage_code === current)) return current;
      return resolvedPreStages[0]?.stage_code || resolvedCastingStages[0]?.stage_code || current;
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    if (activeScreen === 'DELAYED_STAGES') {
      const delayed = await api.fetchDelayedStages();
      setDelayedStages(delayed);
      setJobCards([]);
      setLoading(false);
      return;
    }
    const data = await api.fetchJobCards(activeScreen);
    setJobCards(data);
    setDelayedStages([]);
    setLoading(false);
  }, [activeScreen]);

  const applyStartedStagesToJobCards = useCallback((startedStages) => {
    if (!Array.isArray(startedStages) || startedStages.length === 0) return false;
    const startedMap = new Map(startedStages.map((row) => [Number(row.stage_id), row]));
    let hasAnyMatch = false;

    setJobCards((prev) => prev.map((job) => {
      const nextStages = {};
      let jobChanged = false;

      Object.entries(job.stages || {}).forEach(([groupKey, stages]) => {
        nextStages[groupKey] = (stages || []).map((stageRow) => {
          const stageId = Number(getStageId(stageRow));
          const started = startedMap.get(stageId);
          if (!started) return stageRow;
          hasAnyMatch = true;
          jobChanged = true;
          return {
            ...stageRow,
            worker_name: started.worker_name || stageRow.worker_name,
            status: started.status || stageRow.status,
          };
        });
      });

      return jobChanged ? { ...job, stages: nextStages } : job;
    }));

    return hasAnyMatch;
  }, []);

  const applyCompletedStageTransitionToJobCards = useCallback((transition) => {
    const currentStageId = Number(transition?.currentStageId || 0);
    const nextStageCode = normalizeStageCode(transition?.nextStageCode);
    const skipQc = Boolean(transition?.skipQc);
    if (!currentStageId) return false;

    const updateJobStages = (job) => {
      let jobChanged = false;
      const nextStages = {};

      Object.entries(job?.stages || {}).forEach(([groupKey, stages]) => {
        nextStages[groupKey] = (stages || []).map((stageRow) => {
          const stageRowId = Number(getStageId(stageRow));
          const stageRowCode = normalizeStageCode(stageRow?.stage_code || stageRow?.stage_name);

          if (stageRowId === currentStageId) {
            jobChanged = true;
            return {
              ...stageRow,
              status: skipQc ? 'QC_APPROVED' : 'QC_PENDING',
            };
          }

          if (skipQc && nextStageCode && stageRowCode === nextStageCode && normalizeStageStatus(stageRow?.status) === 'LOCKED') {
            jobChanged = true;
            return {
              ...stageRow,
              status: 'PENDING',
            };
          }

          return stageRow;
        });
      });

      return jobChanged ? { ...job, stages: nextStages } : job;
    };

    let hasAnyMatch = false;

    setJobCards((prev) => prev.map((job) => {
      const updatedJob = updateJobStages(job);
      if (updatedJob !== job) hasAnyMatch = true;
      return updatedJob;
    }));

    setCurrentJob((prev) => (prev ? updateJobStages(prev) : prev));
    setCurrentStage((prev) => {
      if (!prev) return prev;
      const prevId = Number(getStageId(prev));
      if (prevId !== currentStageId) return prev;
      return {
        ...prev,
        status: skipQc ? 'QC_APPROVED' : 'QC_PENDING',
      };
    });

    return hasAnyMatch;
  }, []);

  useEffect(() => {
    loadStageCatalog();
  }, [loadStageCatalog]);

  useEffect(() => {
    if (!activeScreen) return;
    loadData();
  }, [activeScreen, loadData]);

  useEffect(() => {
    const stageCode = searchParams.get('stage_code');
    if (stageCode && (STAGE_SCREEN_MAP[stageCode] || preStageNav.some((stage) => stage.stage_code === stageCode))) {
      setActiveScreen((current) => {
        if (current === stageCode) return current;
        setViewMode('LIST');
        return stageCode;
      });
    }
  }, [preStageNav, searchParams, STAGE_SCREEN_MAP]);

  const handleStartStage = async (stageId, data) => {
    try {
      const isBatchStart = Array.isArray(data.batch_stage_ids) && data.batch_stage_ids.length > 1;
      if (isBatchStart) {
        const res = await api.startStage(stageId, {
          worker_id: data.worker_id,
          additional_workers: data.additional_workers,
          expected_end_time: data.expected_end_time,
          before_weight: data.before_weight,
          remarks: data.remarks,
          issued_materials: data.issued_materials,
          issued_stones: data.issued_stones,
          batch_stage_ids: data.batch_stage_ids
        });
        const updated = applyStartedStagesToJobCards(res?.data?.data?.batch_started_stages || []);
        if (!updated) {
          loadData();
        }
        return;
      }

      const stageIds = Array.isArray(data.stage_ids) && data.stage_ids.length > 0 ? data.stage_ids : [stageId];
      let anyLocalUpdate = false;
      for (const currentStageId of stageIds) {
        if (!currentStageId) {
          throw new Error('Stage ID is missing for one of the selected jobs.');
        }
        const res = await api.startStage(currentStageId, {
          worker_id: data.worker_id,
          additional_workers: data.additional_workers,
          expected_end_time: data.expected_end_time,
          before_weight: data.before_weight,
          remarks: data.remarks,
          issued_materials: data.issued_materials,
          issued_stones: data.issued_stones,
          batch_stage_ids: data.batch_stage_ids
        });
        anyLocalUpdate = applyStartedStagesToJobCards(res?.data?.data?.batch_started_stages || []) || anyLocalUpdate;
      }
      if (!anyLocalUpdate) {
        loadData();
      }
    } catch (e) {
      console.error(e);
      const detailRows = e?.response?.data?.details || [];
      const detailText = detailRows.length > 0
        ? `\n${detailRows.map((row) => `${row.material_name}: need ${row.required_qty}, available ${row.available_qty} ${row.unit || ''}`.trim()).join('\n')}`
        : '';
      const errMsg = (e?.response?.data?.message || e?.message || "Failed to start stage or issue materials.") + detailText;
      alert(errMsg);
    }
  };

  const handleExecuteStage = (job, stage) => {
    setCurrentJob(job);
    setCurrentStage({ ...stage, stage_id: getStageId(stage) });
    setViewMode('EXECUTE');
  };

  const handleSaveStageExecution = async (transition = null) => {
    if (transition) {
      applyCompletedStageTransitionToJobCards(transition);
    }
    setViewMode('LIST');
    loadData();
  };

  const activeStageInfo = STAGE_SCREEN_MAP[activeScreen] || getPreStageInfo(activeScreen, activeScreen);

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      <style>{`
        @media (max-width: ${BREAKPOINTS.mobile}) {
          .pre-sidebar {
            position: fixed;
            inset: 0 auto 0 0;
            width: 250px !important;
            transform: translateX(-100%);
            transition: transform 0.25s ease;
            z-index: 60;
          }
          .pre-sidebar.open {
            transform: translateX(0);
          }
          .pre-content-mobile {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          .pre-mobile-modal {
            width: 100vw !important;
            height: 100vh !important;
            max-width: 100vw !important;
            border-radius: 0 !important;
          }
        }
      `}</style>
      <aside className={`pre-sidebar w-64 bg-white text-slate-900 flex flex-col border-r border-slate-200 shadow-sm z-20 ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm"><Gem className="text-white" size={24} /></div>
          <div>
            <span className="text-xl font-black text-slate-900 tracking-tighter uppercase italic">
              ZILVERRA<span className="text-blue-600">JEWELS</span>
            </span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <div className="text-xs font-bold text-slate-500 uppercase px-3 mt-4 mb-2">Pre-Casting</div>
          {preStagesBeforeCasting.map(stage => (
            <button key={stage.stage_code} onClick={() => { setActiveScreen(stage.stage_code); setViewMode('LIST'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeScreen === stage.stage_code ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-700 hover:bg-slate-100'}`}>
              {React.createElement((STAGE_SCREEN_MAP[stage.stage_code] || stage).icon, { size: 20 })} {(STAGE_SCREEN_MAP[stage.stage_code] || stage).title}
            </button>
          ))}

          {castingStageNav.length > 0 && (
            <>
              <div className="text-xs font-bold text-slate-500 uppercase px-3 mt-6 mb-2">Casting Process</div>
              {castingStageNav.map(stage => (
                <button key={stage.stage_code} onClick={() => { setActiveScreen(stage.stage_code); setViewMode('LIST'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeScreen === stage.stage_code ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-700 hover:bg-slate-100'}`}>
                  {React.createElement((STAGE_SCREEN_MAP[stage.stage_code] || stage).icon, { size: 20 })} {(STAGE_SCREEN_MAP[stage.stage_code] || stage).title}
                </button>
              ))}
            </>
          )}

          {preStagesAfterCasting.map(stage => (
            <button key={stage.stage_code} onClick={() => { setActiveScreen(stage.stage_code); setViewMode('LIST'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeScreen === stage.stage_code ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-700 hover:bg-slate-100'}`}>
              {React.createElement((STAGE_SCREEN_MAP[stage.stage_code] || stage).icon, { size: 20 })} {(STAGE_SCREEN_MAP[stage.stage_code] || stage).title}
            </button>
          ))}

          <div className="text-xs font-bold text-slate-500 uppercase px-3 mt-6 mb-2">Monitoring</div>
          <button onClick={() => { setActiveScreen('DELAYED_STAGES'); setViewMode('LIST'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeScreen === 'DELAYED_STAGES' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-red-600 hover:bg-red-50'}`}>
            <AlertTriangle size={20} className={activeScreen === 'DELAYED_STAGES' ? 'text-red-400' : 'text-current'} /> Delayed Stages
          </button>
        </nav>
      </aside>
      {sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/40 z-50 md:hidden"
          aria-label="Close sidebar"
        />
      )}
      <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
        {viewMode !== 'EXECUTE' && (
          <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 pre-content-mobile shadow-sm z-10">
            <div className="flex items-center gap-2 text-sm">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1 rounded-md hover:bg-slate-100">
                <Menu size={18} />
              </button>
              <span onClick={() => navigate('/production')} className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors">Home</span>
              <span className="text-slate-600">/</span>
              <span className="font-medium text-slate-900">{STAGE_SCREEN_MAP[activeScreen]?.title || 'Dashboard'}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold border border-emerald-100 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> System Online
              </span>
            </div>
          </header>
        )}

        <div className="flex-1 overflow-hidden relative">
          {loading ? (
            <div className="h-full overflow-y-auto bg-white p-8">
              <StageSkeletonGrid />
            </div>
          ) : viewMode === 'LIST' ? (
            <PreCastingDashboard
              jobCards={jobCards}
              delayedStages={delayedStages}
              onStartStage={handleStartStage}
              onExecute={handleExecuteStage}
              stageFilter={activeScreen}
              screenTitle={activeStageInfo.title}
              ScreenIcon={activeStageInfo.icon}
            />
          ) : (
            <PreCastingExecutionScreen
              jobCard={currentJob}
              stage={currentStage}
              onBack={() => setViewMode('LIST')}
              onSave={handleSaveStageExecution}
              stageFlow={workflowStageNav}
            />
          )}
        </div>
      </main>
    </div>
  );
}; 
